/**
 * Phase 4g.C+ (Feb 2026) — Werkstatt-Auftrag-Modal extrahiert aus
 * `WorkshopOrdersPage.jsx` (~535 Zeilen). Beinhaltet WorkOrderModal +
 * PositionRow + DamagePhotoSection.
 *
 * Public Default Export:
 *   <WorkOrderModal order={...?} customers articles employees onClose onSaved />
 *
 * Beim Klick „Speichern" wird `onSaved(savedOrder)` aufgerufen — die Page
 * lädt dann die Liste neu. Schließen ohne Speichern → `onClose()`.
 *
 * Helper-Komponenten (PositionRow, DamagePhotoSection) sind in dieser
 * Datei privat und werden nicht re-exportiert (zukünftig snapshot-testbar).
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeftRight, Barcode, Camera, Car, X, GitBranch, CalendarClock, Loader2, Percent, Plus, FileText, TrendingUp, Search, ChevronsUpDown, Share2, FileSignature, Printer, ShoppingCart, Check, Handshake, Send, Wrench } from "lucide-react";

import api from "../../lib/api";
import { computeHuDue, HuStatusBanner } from "./HuStatusBadge";
import { flashMissingByTestId } from "../../lib/requiredField";
import { clampDiscountPct } from "../../lib/discountRules";
import { AutoContactHint } from "../shared/AutoContactHint";
import { previewPdfResponse, previewPdfRequest, customerRecipient } from "../../lib/pdfPreview";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../ui/command";
import InfoHint from "../listing/InfoHint";
import ShareOrderModal from "./ShareOrderModal";
import FrequentSelect from "../listing/FrequentSelect";
import BrandCombobox from "../listing/BrandCombobox";
import { MOTO_BRANDS, BIKE_BRANDS } from "../../lib/listingConstants";
import { fmtEur, fmtNum, plateLabel } from "./workshopConstants";
import { RecommendPartnerDialog } from "../dealer/RecommendPartnerDialog";
import HandoverProtocolModal from "./HandoverProtocolModal";
import WwsAddBar from "../wms/WwsAddBar";
import BarcodeScanDialog from "./BarcodeScanDialog";
import ArticleModal from "../../pages/wms/ArticleModal";
import { VinWarning } from "../common/VinWarning";

// Marken-Gruppen aus dem Inserate-Formular wiederverwenden (User-Wunsch).
const WO_BRAND_GROUPS = [
    { heading: "Motorrad / Roller", items: MOTO_BRANDS.map((b) => ({ value: b })) },
    { heading: "Fahrrad / E-Bike", items: BIKE_BRANDS.map((b) => ({ value: b })) },
];

// Tätigkeitsbereich-Labels für die Provisions-Verteilung (Phase C-Engine).
const AREA_LABELS = {
    kundendienst: "Kundendienst", teile_zubehoer: "Teile / Zubehör", werkstatt: "Werkstatt",
    verkauf: "Verkauf", disposition: "Disposition", buchhaltung: "Buchhaltung", filialleitung: "Filialleitung",
};


// Jun-2026 (User-Wunsch) — Kunden-Universalsuche im Werkstatt-Auftrag.
// Serverseitige Suche über Name, Firma, E-Mail, Telefon UND Kundennummer
// (Endpoint `/customers?q=`), statt eines einfachen Dropdowns aller Kunden.
function WoCustomerPicker({ value, customers, onChange }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [picked, setPicked] = useState(null);

    const fmtName = (c) => (c.type === "business" && c.company_name
        ? `${c.company_name} — ${c.first_name || ""} ${c.last_name || ""}`.trim()
        : `${c.first_name || ""} ${c.last_name || c.company_name || ""}`.trim()) || "Kunde";

    const current = (picked && picked.id === value)
        ? picked
        : (customers || []).find((c) => c.id === value) || null;
    const label = current ? fmtName(current) : "";

    useEffect(() => {
        const term = query.trim();
        if (term.length < 2) { setResults([]); setLoading(false); return; }
        let active = true;
        setLoading(true);
        const h = setTimeout(async () => {
            try {
                const r = await api.get("/customers", { params: { q: term, limit: 25 } });
                if (active) setResults(r.data || []);
            } catch { if (active) setResults([]); }
            finally { if (active) setLoading(false); }
        }, 250);
        return () => { active = false; clearTimeout(h); };
    }, [query]);

    const pick = (c) => { setPicked(c); onChange(c.id); setOpen(false); setQuery(""); };

    // Jul-2026 (Bugfix): Vorausgewählter Kunde (z. B. Deep-Link aus der Kunden-
    // suche) ist oft nicht in der geladenen Liste — dann direkt nachladen,
    // damit Name & Daten sichtbar übernommen werden.
    useEffect(() => {
        if (!value || (picked && picked.id === value)) return;
        if ((customers || []).some((c) => c.id === value)) return;
        let active = true;
        api.get(`/customers/${value}`).then((r) => { if (active && r.data) setPicked(r.data); }).catch(() => {});
        return () => { active = false; };
    }, [value, customers, picked]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    className="w-full flex items-center justify-between rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm text-left hover:border-gray-400"
                    data-testid="wo-customer"
                >
                    <span className={label ? "text-[#111827] truncate" : "text-gray-400 truncate"}>
                        {label || "Kunde * — suchen (Name, Kundennr., Telefon…)"}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder="Name, Kundennummer, Telefon, E-Mail…"
                        value={query}
                        onValueChange={setQuery}
                        data-testid="wo-customer-search"
                    />
                    <CommandList>
                        {query.trim().length < 2 && (
                            <div className="px-3 py-3 text-xs text-gray-400" data-testid="wo-customer-hint">Mind. 2 Zeichen eingeben…</div>
                        )}
                        {loading && <div className="px-3 py-2 text-xs text-gray-500">Suche…</div>}
                        {!loading && query.trim().length >= 2 && results.length === 0 && (
                            <CommandEmpty>Kein Kunde gefunden</CommandEmpty>
                        )}
                        <CommandGroup>
                            {results.map((c) => (
                                <CommandItem
                                    key={c.id}
                                    value={c.id}
                                    onSelect={() => pick(c)}
                                    className="flex flex-col items-start gap-0.5"
                                    data-testid={`wo-customer-opt-${c.id}`}
                                >
                                    <span className="font-bold text-sm">{fmtName(c)}</span>
                                    <span className="text-[10px] text-gray-500 font-mono-num">
                                        {c.customer_number ? `Nr. ${c.customer_number} · ` : ""}{c.phone || c.email || "—"}{c.city ? ` · ${c.city}` : ""}
                                    </span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}


function WorkOrderModal({ order, customers, articles, employees = [], onClose, onSaved, onBack, onDirectInvoice }) {
    const isEdit = !!order?.id;
    const navigate = useNavigate();
    // Jul-2026 (User-Wunsch): Versand einmalig aussetzen + Rabatt-Regeln
    const [skipConfirmation, setSkipConfirmation] = useState(false);
    const [discountRules, setDiscountRules] = useState([]);
    useEffect(() => {
        api.get("/dealer/discount-rules").then((r) => setDiscountRules(r.data?.rules || [])).catch(() => {});
    }, []);
    // Jul-2026 (User-Wunsch) — WWS-Preisschutz: max. Rabatt auf vorgegebene
    // Artikelpreise aus den WWS-Einstellungen (Default 20 %).
    const [maxWwsDiscount, setMaxWwsDiscount] = useState(20);
    useEffect(() => {
        api.get("/wms/settings").then((r) => {
            const v = Number(r.data?.max_sale_discount_pct);
            if (!isNaN(v) && v > 0) setMaxWwsDiscount(v);
        }).catch(() => {});
    }, []);
    const [customerId, setCustomerId] = useState(order?.customer_id || "");
    const [protoOpen, setProtoOpen] = useState(false);
    const [vehicle, setVehicle] = useState({
        brand: "", model: "", year: "", first_registration: "", license_plate: "", vin: "",
        mileage: "", color: "", category: "", tuev_until: "", ...(order?.vehicle || {}),
    });
    const [positions, setPositions] = useState(order?.positions?.length
        ? order.positions.map((p) => ({ ...p }))
        : [{ type: "labour", description: "", quantity: 1, unit: "AW", unit_price_net: 0, vat_rate: 19, discount_percent: 0, aw_count: 0 }]);
    const [customerComplaint, setCustomerComplaint] = useState(order?.customer_complaint || "");
    const [showRecommend, setShowRecommend] = useState(false); // Partner an Kunden empfehlen
    const [employee, setEmployee] = useState(order?.employee || "");
    const [employeeId, setEmployeeId] = useState(order?.employee_id || "");
    const [suggestion, setSuggestion] = useState(null); // { specialty, candidates: [...] }
    const [internalNote, setInternalNote] = useState(order?.internal_note || "");
    const [warrantyManufacturer, setWarrantyManufacturer] = useState(order?.warranty_manufacturer || "");
    const [internalVehicleListingId, setInternalVehicleListingId] = useState(order?.internal_vehicle_listing_id || "");
    const [parkingSpot, setParkingSpot] = useState(order?.parking_spot || "");
    // Aug-2026 (Testing-Agent): kontrollierter Fokus-State statt e.target.type-Mutation
    // (React stellt sonst das type-Prop wieder her — Monats-/Datums-Picker öffnete nicht).
    const [tuevFocused, setTuevFocused] = useState(false);
    const [wishFocused, setWishFocused] = useState(false);
    const [winterInDate, setWinterInDate] = useState(order?.winter_storage_in_date || new Date().toISOString().slice(0, 10));
    const [winterPlannedOut, setWinterPlannedOut] = useState(order?.winter_storage_planned_out_date || "");
    // Jun-2026 (User-Wunsch) — Winterlager-Datumsfelder nur zeigen, wenn eine
    // „Winter-Einlagerung"-Position erfasst wurde.
    const hasWinterCheckin = positions.some(
        (p) => p.winter_storage_role === "checkin" || /winterlager|einlager/i.test(p.description || ""),
    );
    const [estimatedCompletion, setEstimatedCompletion] = useState(order?.estimated_completion || "");
    // Aug-2026 (User-Wunsch) — fester Werkstatt-Termin schon bei der Annahme:
    // erscheint direkt im Kalender (nicht mehr „Nicht zugeordnet") und zählt
    // für Auslastung + Buchungslink-Ampel.
    // Aug-2026 (Bugfix Zeitzonen-Portabilität): Termin-Wandzeit ist IMMER
    // Europe/Berlin — unabhängig von der Gerätezeitzone (DST-sicher via Intl).
    const BERLIN_TZ = "Europe/Berlin";
    const berlinWallToIso = (dayIso, hhmm) => {
        const pretendUtc = new Date(`${dayIso}T${hhmm}:00Z`);
        const g = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
            timeZone: BERLIN_TZ, year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
        }).formatToParts(pretendUtc).map((p) => [p.type, p.value]));
        const asUtc = Date.UTC(+g.year, +g.month - 1, +g.day, +g.hour % 24, +g.minute, +g.second);
        return new Date(pretendUtc.getTime() - (asUtc - pretendUtc.getTime())).toISOString();
    };
    const isoToBerlinWall = (iso) => {
        try {
            const g = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
                timeZone: BERLIN_TZ, year: "numeric", month: "2-digit", day: "2-digit",
                hour: "2-digit", minute: "2-digit", hourCycle: "h23",
            }).formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
            return { date: `${g.year}-${g.month}-${g.day}`, time: `${g.hour}:${g.minute}` };
        } catch { return { date: "", time: "" }; }
    };
    const _apptWall = order?.appointment_at ? isoToBerlinWall(order.appointment_at) : { date: "", time: "" };
    const [apptDate, setApptDate] = useState(_apptWall.date);
    const [apptTime, setApptTime] = useState(_apptWall.time);
    const [apptDuration, setApptDuration] = useState(order?.duration_min || 60);
    const [apptResource, setApptResource] = useState(order?.resource_id || "");
    // Aug-2026 (User-Wunsch): Termin-Felder erst per Häkchen ausklappen
    const [apptOpen, setApptOpen] = useState(Boolean(_apptWall.date));
    const [calResources, setCalResources] = useState([]);
    // Aug-2026 (User-Wunsch): Lieferanten für die Anzeige an Teil-Positionen
    const [suppliersById, setSuppliersById] = useState({});
    useEffect(() => {
        api.get("/workshop/resources").then((r) => setCalResources(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        api.get("/suppliers").then((r) => {
            const m = {};
            (Array.isArray(r.data) ? r.data : []).forEach((s) => { m[s.id] = s; });
            setSuppliersById(m);
        }).catch(() => {});
    }, []);
    // Aug-2026 (User-Wunsch) — Konflikt-Warnung schon bei der Annahme: prüft
    // live, ob Monteur oder Ressource zum gewählten Termin bereits verplant sind.
    const [apptConflicts, setApptConflicts] = useState([]);
    useEffect(() => {
        if (!apptDate || !apptTime || (!employeeId && !apptResource)) { setApptConflicts([]); return; }
        const h = setTimeout(async () => {
            try {
                const r = await api.get(`/workshop/calendar?from=${apptDate}&to=${apptDate}`);
                const startMs = new Date(berlinWallToIso(apptDate, apptTime)).getTime();
                const endMs = startMs + (Number(apptDuration) || 60) * 60000;
                const hits = (r.data?.scheduled || []).filter((o) => {
                    if (o.id === order?.id) return false;
                    if (["cancelled", "invoiced"].includes(o.status)) return false;
                    const sameEmp = employeeId && o.employee_id === employeeId;
                    const sameRes = apptResource && o.resource_id === apptResource;
                    if (!sameEmp && !sameRes) return false;
                    const oStart = new Date(o.appointment_at).getTime();
                    const oEnd = oStart + (Number(o.duration_min) || 60) * 60000;
                    return oStart < endMs && startMs < oEnd;
                }).map((o) => ({
                    order_number: o.order_number,
                    time: new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", hour: "2-digit", minute: "2-digit" }).format(new Date(o.appointment_at)),
                    kind: (employeeId && o.employee_id === employeeId) ? "Monteur" : "Ressource",
                }));
                setApptConflicts(hits);
            } catch { setApptConflicts([]); }
        }, 400);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apptDate, apptTime, apptDuration, apptResource, employeeId]);
    const [documents] = useState(order?.documents || []);
    const [busy, setBusy] = useState(false);
    // Jun-2026 (User-Wunsch c) — bei „kein Treffer" im WWS-Suchfeld: WWS-Artikel
    // anlegen (Dialog, landet im Lager + als Position) oder freie Position.
    const [newArticle, setNewArticle] = useState(null);

    // Jun-2026 (User-Wunsch) — Ersatz-/Mietfahrzeug bei Auftragsanlage
    const [rentalEnabled, setRentalEnabled] = useState(false);
    const [rentalVehicles, setRentalVehicles] = useState([]);
    const [rentalArticles, setRentalArticles] = useState([]);
    const [rentalVehicleId, setRentalVehicleId] = useState("");
    const [rentalArticleId, setRentalArticleId] = useState("");
    const _td = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
    const [rentalStart, setRentalStart] = useState(_td(0));
    const [rentalEnd, setRentalEnd] = useState(_td(1));
    useEffect(() => {
        if (!rentalEnabled || rentalVehicles.length || rentalArticles.length) return;
        let active = true;
        Promise.all([
            api.get("/dealer/rental-vehicles"),
            api.get("/dealer/rental-articles"),
        ]).then(([v, a]) => {
            if (!active) return;
            const avail = (v.data.groups || []).flatMap((g) => g.vehicles).filter((veh) => veh.status === "verfuegbar");
            setRentalVehicles(avail);
            setRentalArticles(a.data.items || []);
            if (avail[0]) setRentalVehicleId(avail[0].id);
            if ((a.data.items || [])[0]) setRentalArticleId(a.data.items[0].id);
        }).catch(() => {});
        return () => { active = false; };
    }, [rentalEnabled]);
    // Jun-2026 (User-Wunsch) — Kunden-Garage: Fahrzeuge des gewählten Kunden
    const [garage, setGarage] = useState([]);
    const [selectedVehicleId, setSelectedVehicleId] = useState(null);

    useEffect(() => {
        if (!customerId) { setGarage([]); return; }
        let active = true;
        api.get(`/customers/${customerId}/vehicles`)
            .then((r) => {
                if (!active) return;
                const list = r.data || [];
                setGarage(list);
                // Auto-Befüllung (User-Wunsch): bei NEUEM Auftrag mit noch leerem
                // Fahrzeug das jüngste Garage-Fahrzeug direkt übernehmen.
                // Jul-2026: Hat der Kunde genau EIN Fahrzeug, werden die Daten auch
                // beim Bearbeiten vorbelegt, solange noch keine erfasst sind.
                const empty = !vehicle.brand && !vehicle.vin && !vehicle.license_plate;
                const nearlyEmpty = !vehicle.vin && !vehicle.license_plate && !vehicle.model;
                if ((!order?.id && empty && list.length > 0) || (nearlyEmpty && list.length === 1)) {
                    // Optional: gezielt vorgewähltes Fahrzeug (aus CRM-Garage), sonst jüngstes.
                    const preset = order?._preselect_vehicle_id
                        ? list.find((v) => v.id === order._preselect_vehicle_id)
                        : null;
                    pickGarageVehicle(preset || list[0]);
                }
            })
            .catch(() => { if (active) setGarage([]); });
        return () => { active = false; };
    }, [customerId]);

    const pickGarageVehicle = (v) => {
        setSelectedVehicleId(v.id);
        setVehicle({
            brand: v.brand || "", model: v.model || "", year: v.year || "",
            first_registration: (v.first_registration || "").slice(0, 10),
            license_plate: v.license_plate || "", vin: v.vin || "",
            mileage: v.mileage || "", color: v.color || "",
            hsn: v.hsn || "", tsn: v.tsn || "", category: v.category || "",
            tuev_until: (v.tuev_until || v.hu_date || "").slice(0, 7),
        });
    };
    const newGarageVehicle = () => {
        setSelectedVehicleId(null);
        setVehicle({ brand: "", model: "", year: "", first_registration: "", license_plate: "", vin: "", mileage: "", color: "", category: "", tuev_until: "" });
    };

    // Nur Mechaniker & Kundendienst sind als verantwortlicher Mechaniker zuweisbar (User-Wunsch)
    const assignableEmployees = (employees || []).filter(
        (e) => ["mechaniker", "kundendienst"].includes(e.employee_function));

    // ---- Auftrags-Presets (· 265): Schnell-Chips, „Häufig genutzt", Vorlagen ----
    const [woQuick, setWoQuick] = useState([]);
    const [woFrequent, setWoFrequent] = useState([]);
    useEffect(() => {
        api.get("/dealer/work-order-quick-positions").then((r) => setWoQuick(r.data?.positions || [])).catch(() => {});
    }, []);
    // „Häufig genutzt" kontextsensitiv je Fahrzeugtyp nachladen
    useEffect(() => {
        const cat = vehicle.category || "";
        api.get(`/dealer/work-order-positions/frequent?limit=8${cat ? `&category=${cat}` : ""}`)
            .then((r) => setWoFrequent(r.data?.positions || []))
            .catch(() => setWoFrequent([]));
    }, [vehicle.category]);

    // Jul-2026 (User-Wunsch) — Lohn-/AW-Satz aus den Einstellungen (WWS →
    // Kalkulation → Werkstatt-Stundensatz): befüllt neue Lohn-Positionen automatisch.
    const [awRate, setAwRate] = useState(null);
    useEffect(() => {
        api.get("/wms/montage-rate").then((r) => {
            const a = r.data;
            if (!a) return;
            const net = Number(a.sell_price_net || ((a.sell_price_gross || 0) / (1 + (a.vat_rate || 19) / 100)) || 0);
            if (net > 0) {
                const v = Math.round(net * 100) / 100;
                // Aug-2026 (User-Wunsch): vorgemerkter Rabatt auf offizielle AW-Vorgaben.
                const discountPct = Number(a.official_aw_discount_pct) || 0;
                setAwRate({ net: v, hourly: a.hourly_rate || null, discountPct });
                // Aug-2026 (User-Bug „AW-Preis zeigt 0"): bereits vorhandene leere
                // Lohn-Zeilen (Start-Zeile) nachträglich mit dem Satz befüllen.
                setPositions((arr) => arr.map((p) => (
                    p.type === "labour" && !p.article_id
                    && !String(p.description || "").trim()
                    && !(Number(p.unit_price_net) > 0)
                        ? { ...p, unit_price_net: v, _price_autofilled: true, discount_percent: Number(p.discount_percent) > 0 ? p.discount_percent : discountPct } : p)));
            }
        }).catch(() => {});
    }, []);

    const appendWoPosition = (p) => setPositions((arr) => {
        const blankIdx = arr.findIndex((x) => !String(x.description || "").trim());
        const isLab = p.type !== "part";
        const ni = {
            type: p.type === "part" ? "part" : "labour",
            description: p.description, quantity: p.quantity ?? 1,
            unit: p.unit || (p.type === "part" ? "Stück" : "AW"),
            // Aug-2026 (User-Wunsch): Der Netto-AW-Satz aus den Einstellungen hat
            // IMMER Vorrang — auch wenn Vorlagen/„häufige Positionen" noch alte
            // (Brutto-)Preise gespeichert haben.
            unit_price_net: isLab && awRate ? awRate.net : (p.unit_price_net ?? 0),
            vat_rate: p.vat_rate ?? 19,
            discount_percent: isLab && awRate?.discountPct ? awRate.discountPct : 0,
            billing_category: p.billing_category || "customer",
        };
        if (blankIdx >= 0) return arr.map((x, i) => i === blankIdx ? ni : x);
        return [...arr, ni];
    });
    const addFreeWoPosition = (text) => {
        appendWoPosition({ type: "part", description: text, quantity: 1, unit: "Stück", unit_price_net: 0, vat_rate: 19, billing_category: "customer" });
        toast.success("Freie Position hinzugefügt");
    };
    // HU/TÜV-Vorbereitung als Position übernehmen — bevorzugt eine konfigurierte
    // „häufige" Position (mit hinterlegtem Preis), sonst Standard-Position.
    const addHuPosition = () => {
        const match = (woFrequent || []).find((p) => /hu|tüv|tuev/i.test(p.description || ""));
        appendWoPosition(match || {
            type: "labour", description: "HU/TÜV-Vorbereitung", quantity: 1, unit: "AW",
            unit_price_net: 0, vat_rate: 19, billing_category: "customer",
        });
        toast.success('Position „HU/TÜV-Vorbereitung" hinzugefügt');
    };
    const setVeh = (k, v) => setVehicle((x) => ({ ...x, [k]: v }));

    // Aug-2026 (User-Wunsch) — TÜV/HU-Hinweis automatisch in den Kundenauftrag
    // übernehmen (nur bei Neuerfassung; wird nicht doppelt eingefügt).
    useEffect(() => {
        if (order?.id) return;
        const hu = computeHuDue(vehicle);
        if (!hu) return;
        setCustomerComplaint((prev) => {
            if (/tüv|tuev|\bhu\b|hauptuntersuchung/i.test(prev || "")) return prev;
            const line = `TÜV/HU ${hu.label}`;
            return (prev || "").trim() ? `${(prev || "").trim()}\n${line}` : line;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicle.tuev_until, vehicle.first_registration, vehicle.category]);

    // Jul-2026 (User-Wunsch) — Werkstatt-FIN-Autofill + Plausibilitätsprüfung
    // (auch nach ZB1-Scan): Wissensdatenbank-Abgleich über das FIN-Präfix.
    const [vkCheck, setVkCheck] = useState(null);
    // Jul-2026 (User-Wunsch) — Leasing-/Finanzierungs-Ausläufer: Fahrzeug noch
    // in Kundennutzung? + Wartungsplan-Fälligkeiten aus der Wissensdatenbank.
    const [usageInfo, setUsageInfo] = useState(null);
    const [maintDue, setMaintDue] = useState(null);
    useEffect(() => {
        const vin = (vehicle.vin || "").replace(/[^A-Za-z0-9]/g, "");
        const plate = vehicle.license_plate || "";
        if (!vin && !plate && !customerId) { setUsageInfo(null); return undefined; }
        const t = setTimeout(async () => {
            try {
                let cname = "";
                if (customerId) {
                    try {
                        const c = await api.get(`/customers/${customerId}`);
                        cname = c.data?.name || `${c.data?.first_name || ""} ${c.data?.last_name || ""}`.trim();
                    } catch { /* optional */ }
                }
                const r = await api.get("/workshop/usage-check", { params: { vin, plate, customer_name: cname } });
                setUsageInfo(r.data?.in_use ? r.data : null);
            } catch { setUsageInfo(null); }
        }, 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicle.vin, vehicle.license_plate, customerId]);
    useEffect(() => {
        if (!vehicle.brand && !vehicle.model) { setMaintDue(null); return undefined; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/maintenance-plans/check", {
                    params: {
                        brand: vehicle.brand || "", model: vehicle.model || "",
                        km: Number(vehicle.mileage) || 0,
                        first_registration: vehicle.first_registration || "",
                    },
                });
                setMaintDue(r.data?.plan ? r.data : null);
            } catch { setMaintDue(null); }
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicle.brand, vehicle.model, vehicle.mileage, vehicle.first_registration]);
    const [vinHistory, setVinHistory] = useState([]);
    // Jul-2026 (User-Wunsch) — FIN-Duplikat-Warnung: FIN existiert schon an einem Standort
    const [vinDup, setVinDup] = useState([]);
    // Jun-2026 (User-Wunsch) — Finanzierungs-/Leasing-Status des Fahrzeugs bei der Erfassung
    const [financeStatus, setFinanceStatus] = useState(null);
    useEffect(() => {
        const vin = (vehicle.vin || "").replace(/[^A-Za-z0-9]/g, "");
        if (vin.length < 10) { setVkCheck(null); setVinHistory([]); setVinDup([]); setFinanceStatus(null); return undefined; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/vehicle-knowledge/lookup", { params: { vin } });
                if (!r.data.found) { setVkCheck(null); } else {
                    const it = r.data.item;
                    const norm = (s) => String(s || "").toLowerCase().trim();
                    const brandMismatch = vehicle.brand && it.manufacturer
                        && !norm(vehicle.brand).includes(norm(it.manufacturer))
                        && !norm(it.manufacturer).includes(norm(vehicle.brand));
                    setVkCheck({ ...it, mismatch: !!brandMismatch });
                    setVehicle((x) => {
                        const n = { ...x };
                        let changed = false;
                        if (!x.brand && it.manufacturer) { n.brand = it.manufacturer; changed = true; }
                        if (!x.model && it.model) { n.model = it.model; changed = true; }
                        if (!x.year && it.model_year) { n.year = String(it.model_year); changed = true; }
                        return changed ? n : x;
                    });
                }
            } catch { /* Lookup optional */ }
            // Kunden-FIN-Historie: frühere Aufträge desselben Fahrzeugs (Jul-2026)
            if (vin.length >= 11) {
                try {
                    const hr = await api.get("/work-orders/vehicle-history", { params: { vin, exclude_id: order?.id || "" } });
                    setVinHistory(hr.data.items || []);
                } catch { setVinHistory([]); }
            } else {
                setVinHistory([]);
            }
            try {
                const dr = await api.get("/customer-vehicles/vin-check", { params: { vin } });
                setVinDup(dr.data.matches || []);
            } catch { setVinDup([]); }
            try {
                const fr = await api.get("/work-orders/vehicle-finance-status", { params: { vin } });
                setFinanceStatus((fr.data.financing || fr.data.leasing) ? fr.data : null);
            } catch { setFinanceStatus(null); }
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicle.vin, vehicle.brand]);
    // Aug-2026 (User-Wunsch): Fahrzeug-spezifischer Direktsprung ins
    // Lieferanten-Portal (N+F heute, weitere Lieferanten folgen).
    const [portalLinks, setPortalLinks] = useState([]);
    // Aug-2026 (User-Wunsch): Bereiche auf dem Werkstatt-Tablet (unter md) einklappbar
    const [secOpen, setSecOpen] = useState({ kunde: true, daten: true, teile: true });
    useEffect(() => {
        const brand = String(vehicle.brand || "").trim();
        if (!brand) { setPortalLinks([]); return undefined; }
        const h = setTimeout(async () => {
            try {
                const r = await api.get("/dealer/supplier-portal-links",
                    { params: { brand, model: String(vehicle.model || "").trim() } });
                setPortalLinks(r.data?.links || []);
            } catch { setPortalLinks([]); }
        }, 400);
        return () => clearTimeout(h);
    }, [vehicle.brand, vehicle.model]);
    const setPos = (i, k, v) => {
        // Jul-2026 (User-Wunsch): Rabatt-Höchstsätze aus den Einstellungen anwenden.
        if (k === "discount_percent" && discountRules.length) {
            const p = positions[i] || {};
            const res = clampDiscountPct(discountRules, p.type === "labour" ? "labour" : "part", p.quantity, p.unit_price_net, Number(v));
            if (res) { toast.error(`Rabatt begrenzt auf ${res.pct} % (Regel: ${res.ruleName})`); v = res.pct; }
        }
        // Jul-2026 (User-Wunsch) — WWS-Preisschutz: max. Rabatt auf Artikelpreise.
        if (k === "discount_percent" && (positions[i] || {}).article_id && Number(v) > maxWwsDiscount) {
            toast.error(`Maximaler Rabatt auf Artikelpreise: ${maxWwsDiscount} % (WWS-Einstellungen)`);
            v = maxWwsDiscount;
        }
        setPositions((arr) => arr.map((p, idx) => idx === i
            ? { ...p, [k]: v, ...(k === "description" ? { _desc_from_complaint: false } : {}) }
            : p));
    };
    // Aug-2026 (User-Wunsch): Kundenauftrags-Text automatisch als Beschreibung der
    // ERSTEN Lohn-Position übernehmen (solange dort nichts Eigenes eingetragen wurde).
    useEffect(() => {
        const txt = String(customerComplaint || "").trim();
        if (!txt) return;
        setPositions((arr) => {
            const i = arr.findIndex((p) => p.type === "labour" && !p.article_id
                && (p._desc_from_complaint || !String(p.description || "").trim()));
            if (i === -1) return arr;
            if (String(arr[i].description || "") === txt) return arr;
            const next = [...arr];
            next[i] = { ...next[i], description: txt, _desc_from_complaint: true };
            return next;
        });
    }, [customerComplaint]);
    const addPos = (type = "part") => setPositions((arr) => {
        // Aug-2026 (User-Wunsch): die ERSTE Lohn-Position übernimmt den
        // Kundenauftrags-Text als Beschreibung (auch beim Hinzufügen).
        const complaint = String(customerComplaint || "").trim();
        const firstLabour = type === "labour" && !arr.some((p) => p.type === "labour" && !p.article_id);
        return [...arr, {
            type, description: firstLabour && complaint ? complaint : "",
            ...(firstLabour && complaint ? { _desc_from_complaint: true } : {}),
            quantity: 1, unit: type === "labour" ? "AW" : "Stück",
            // Lohn: EP netto automatisch aus dem Einstellungs-AW-Satz (falls hinterlegt)
            unit_price_net: type === "labour" && awRate ? awRate.net : 0,
            vat_rate: 19, discount_percent: type === "labour" && awRate?.discountPct ? awRate.discountPct : 0,
            billing_category: "customer",
        }];
    });
    const removePos = (i) => setPositions((arr) => arr.filter((_, idx) => idx !== i));

    // Jul-2026 (User-Wunsch) — Partner-Preisliste: Standard-Leistungen der
    // Partnerunternehmen direkt als Position übernehmen.
    const [partnerSvcOpen, setPartnerSvcOpen] = useState(false);
    const [partnerServices, setPartnerServices] = useState(null);
    const loadPartnerServices = async () => {
        if (partnerServices !== null) return;
        try {
            const r = await api.get("/dealers/me/partners");
            const flat = [];
            (r.data?.items || []).forEach((p) => (p.price_list || []).forEach((it) => flat.push({ partner: p, item: it })));
            setPartnerServices(flat);
        } catch { setPartnerServices([]); }
    };
    const addPartnerService = (p, it) => {
        setPositions((arr) => [...arr, {
            type: "labour", description: `${p.name}: ${it.name}`, quantity: 1,
            unit: "Pauschal", unit_price_net: Number(it.price_net) || 0,
            vat_rate: Number(it.vat_rate) || 19, discount_percent: 0,
            billing_category: "customer",
        }]);
        setPartnerSvcOpen(false);
        toast.success(`Partner-Leistung „${it.name}" übernommen`);
    };
    const linkArticle = (i, art) => {
        if (!art || !art.id) return;
        const t = art.category === "labour" ? "labour" : "part";
        setPositions((arr) => {
            const next = arr.map((p, idx) => idx === i ? {
                ...p,
                type: t,
                article_id: art.id,
                article_sku: art.sku,
                winter_storage_role: art.winter_storage_role || null,
                description: art.name,
                // Aug-2026 (User-Regel): echter Lohn = immer „AW"; Dienstleistungen
                // (Service-Artikel mit anderer Einheit) = „Stück" — beides fix.
                unit: t === "labour"
                    ? ((art.unit && art.unit !== "AW") ? "Stück" : "AW")
                    : (art.category === "werkstatt_service" ? "Stück" : (art.unit || "Stück")),
                unit_price_net: Number(art.sell_price_net || (art.sell_price_gross / (1 + (art.vat_rate || 19) / 100))).toFixed(2),
                _min_price_net: Number(Number(art.sell_price_net || (art.sell_price_gross / (1 + (art.vat_rate || 19) / 100)) || 0).toFixed(2)),
                vat_rate: art.vat_rate || 19,
            } : p);
            // Aug-2026 (User-Wunsch): Lohn-Zeile (AW) direkt unter dem Artikel öffnen
            if (t === "part" && !next[i + 1]?._auto_labour) {
                next.splice(i + 1, 0, {
                    type: "labour", description: `Lohn: ${art.name}`, quantity: "",
                    unit: "AW", unit_price_net: awRate ? awRate.net : 0, vat_rate: 19,
                    discount_percent: awRate?.discountPct || 0,
                    billing_category: next[i].billing_category || "customer",
                    _auto_labour: true,
                });
            }
            return next;
        });
    };
    // Teil per Kamera/Barcode scannen (EAN oder SKU) → Position i füllen.
    // Nicht gefunden (User-Wunsch 3b): neue Teil-Position mit Code als Artikelnummer.
    const resolveScan = async (i, code) => {
        const c = String(code || "").trim();
        if (!c) return;
        let art = (articles || []).find((a) => a.is_active && (
            String(a.ean || "").trim() === c ||
            String(a.sku || "").trim().toLowerCase() === c.toLowerCase()));
        if (!art) {
            try { const r = await api.get(`/articles/by-ean/${encodeURIComponent(c)}`); art = r.data; } catch { /* nicht gefunden */ }
        }
        if (art && art.id) {
            linkArticle(i, art);
            toast.success(`${art.name} übernommen`);
        } else {
            setPositions((arr) => arr.map((p, idx) => idx === i ? { ...p, type: "part", article_sku: c } : p));
            toast.info(`Artikel „${c}" nicht gefunden – Artikelnummer übernommen. Bitte Bezeichnung & Preis ergänzen.`);
        }
    };
    // Jun-2026 (User-Wunsch) — Warenwirtschafts-Universalsuche + EAN-Scan:
    // EIN Suchfeld über ALLE Daten (Lohn UND Teile); der Typ ergibt sich aus der
    // Artikel-Kategorie. Neue Positionen erben die Auftragsart (billing_category)
    // der ersten Buchung und können danach geändert werden.
    const addArticlePosition = (art) => {
        if (!art) return;
        const t = art.category === "labour" ? "labour" : "part";
        const base = {
            type: t,
            description: art.name,
            quantity: 1,
            unit: t === "labour"
                ? ((art.unit && art.unit !== "AW") ? "Stück" : "AW")
                : (art.category === "werkstatt_service" ? "Stück" : (art.unit || "Stück")),
            unit_price_net: Number(art.sell_price_net || (art.sell_price_gross / (1 + (art.vat_rate || 19) / 100)) || 0).toFixed(2),
            _min_price_net: Number(Number(art.sell_price_net || (art.sell_price_gross / (1 + (art.vat_rate || 19) / 100)) || 0).toFixed(2)),
            vat_rate: art.vat_rate || 19,
            discount_percent: 0,
            article_id: art.id,
            article_sku: art.sku,
            winter_storage_role: art.winter_storage_role || null,
        };
        setPositions((arr) => {
            const inheritCat = arr[0]?.billing_category || "customer";
            const ni = { ...base, billing_category: inheritCat };
            // Aug-2026 (User-Wunsch): unter jedem Artikel eine (weg-klickbare)
            // Lohn-Zeile öffnen, um die angefallenen AW direkt zu erfassen.
            const autoLab = t === "part" ? [{
                type: "labour", description: `Lohn: ${art.name}`, quantity: "",
                unit: "AW", unit_price_net: awRate ? awRate.net : 0, vat_rate: 19,
                discount_percent: awRate?.discountPct || 0, billing_category: inheritCat, _auto_labour: true,
            }] : [];
            const blankIdx = arr.findIndex((x) => !String(x.description || "").trim());
            if (blankIdx >= 0) {
                const next = arr.map((x, i) => i === blankIdx ? ni : x);
                next.splice(blankIdx + 1, 0, ...autoLab);
                return next;
            }
            return [...arr, ni, ...autoLab];
        });
    };
    const addByEan = async (code) => {
        const ean = String(code || "").trim();
        if (!ean) return;
        let art = (articles || []).find((a) => a.is_active && String(a.ean || "").trim() === ean);
        if (!art) {
            try { const r = await api.get(`/articles/by-ean/${encodeURIComponent(ean)}`); art = r.data; } catch { /* not found */ }
        }
        if (!art) { toast.error(`Kein Artikel mit EAN ${ean} gefunden.`); return; }
        addArticlePosition(art);
        toast.success(`${art.name} hinzugefügt`);
    };

    // Live totals
    const totals = positions.reduce((acc, p) => {
        const qty = Number(p.quantity) || 0;
        const net = qty * (Number(p.unit_price_net) || 0);
        const disc = net * ((Number(p.discount_percent) || 0) / 100);
        const ln = net - disc;
        const lv = ln * (Number(p.vat_rate) || 0) / 100;
        return { net: acc.net + ln, vat: acc.vat + lv };
    }, { net: 0, vat: 0 });
    const totalGross = totals.net + totals.vat;

    // Jul-2026 (User-Wunsch): Gutschein-Einlösung nur noch in der Rechnungs-Maske.

    // Juni-2026 (User-Wunsch) — Werkstatt-Provision live je Auftrag aus der
    // WMS-Kategorie-% (Lohn/Teile) + Verteilung auf Tätigkeitsbereiche.
    const [commCfg, setCommCfg] = useState(null);
    useEffect(() => {
        api.get("/dealer/commission-config")
            .then((r) => setCommCfg(r.data.config))
            .catch(() => setCommCfg(null));
    }, []);

    const commByCat = positions.reduce((acc, p) => {
        const qty = Number(p.quantity) || 0;
        const net = qty * (Number(p.unit_price_net) || 0);
        const ln = net - net * ((Number(p.discount_percent) || 0) / 100);
        const cat = p.type === "labour" ? "labour" : p.type === "part" ? "part" : null;
        if (!cat) return acc;
        const pct = Number(commCfg?.category_percents?.[cat] || 0);
        if (pct <= 0) return acc;
        acc[cat] = (acc[cat] || 0) + ln * pct / 100;
        return acc;
    }, {});
    const commissionTotal = Object.values(commByCat).reduce((a, b) => a + b, 0);
    const commissionDist = commCfg && commissionTotal > 0
        ? Object.entries(commCfg.distribution || {})
            .filter(([, v]) => Number(v) > 0)
            .map(([k, v]) => ({ k, label: AREA_LABELS[k] || k, pct: Number(v), eur: commissionTotal * Number(v) / 100 }))
        : [];

    const buildPayload = () => {
        const payload = {
            customer_id: customerId,
            vehicle: {
                ...vehicle,
                year: vehicle.year ? Number(vehicle.year) : null,
                mileage: vehicle.mileage ? Number(vehicle.mileage) : null,
            },
            positions: positions
                // Aug-2026: automatisch geöffnete Lohn-Zeilen ohne erfasste AW
                // werden beim Speichern stillschweigend entfernt.
                .filter((p) => !(p._auto_labour && !(Number(p.quantity) > 0)))
                .map((p) => ({
                type: p.type || "part",
                description: p.description,
                quantity: Number(p.quantity) || 0,
                // Aug-2026 (User-Regel): Lohn wird IMMER in AW erfasst;
                // Dienstleistungen (Service-Artikel) immer in „Stück" — fix.
                unit: (p.type === "labour")
                    ? ((p.article_id && p.unit && p.unit !== "AW") ? "Stück" : "AW")
                    : (p.unit || "Stück"),
                unit_price_net: Math.max(Number(p.unit_price_net) || 0, Number(p._min_price_net) || 0),
                vat_rate: Number(p.vat_rate) || 19,
                discount_percent: Number(p.discount_percent) || 0,
                article_id: p.article_id || null,
                article_sku: p.article_sku || null,
                winter_storage_role: p.winter_storage_role || null,
                aw_count: p.aw_count !== undefined && p.aw_count !== "" ? Number(p.aw_count) : null,
                damage_note: p.damage_note || null,
                damage_images: p.damage_images || [],
                billing_category: p.billing_category || "customer",
            })),
            customer_complaint: customerComplaint || null,
            employee: employee || null,
            employee_id: employeeId || null,
            internal_note: internalNote || null,
            warranty_manufacturer: warrantyManufacturer || null,
            internal_vehicle_listing_id: internalVehicleListingId || null,
            parking_spot: parkingSpot || null,
            winter_storage_in_date: hasWinterCheckin ? (winterInDate || null) : null,
            winter_storage_planned_out_date: hasWinterCheckin ? (winterPlannedOut || null) : null,
            estimated_completion: estimatedCompletion || null,
            documents: documents || [],
            is_quick_capture: !!order?.is_quick_capture,
        };
        Object.keys(payload.vehicle).forEach((k) => {
            if (payload.vehicle[k] === "" || payload.vehicle[k] === null) delete payload.vehicle[k];
        });
        return payload;
    };

    // Jul-2026 (User-Wunsch) — grüner Direkt-Button: speichert die aktuellen
    // Eingaben und springt sofort in die Rechnungserstellung (Vorschau).
    const directInvoice = async () => {
        if (order?.invoice_id) { toast.info("Auftrag wurde bereits abgerechnet"); return; }
        setBusy(true);
        try {
            const payload = buildPayload();
            payload.positions = (payload.positions || []).filter((p) => (p.description || "").trim());
            await api.patch(`/work-orders/${order.id}`, payload);
            const r = await api.get(`/work-orders/${order.id}`);
            onDirectInvoice?.(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        }
        setBusy(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!customerId) { toast.error("Bitte Kunde wählen"); return; }
        // Jun-2026 (User-Wunsch): Mechaniker & Kundenauftrag sind Pflichtfelder,
        // die Positions-Beschreibung dagegen nicht.
        if (!customerComplaint?.trim()) { toast.error("Kundenauftrag ist ein Pflichtfeld"); flashMissingByTestId("wo-complaint"); return; }
        // Aug-2026 (User-Wunsch): unplausible Erstzulassung nur HINWEISEN, nicht blockieren.
        const frYear = Number((vehicle.first_registration || "").slice(0, 4));
        if (frYear && (frYear < 1950 || frYear > new Date().getFullYear())) {
            toast.warning(`Hinweis: Erstzulassung ${(vehicle.first_registration || "").slice(0, 10)} wirkt unplausibel — der Auftrag wird trotzdem gespeichert.`);
        }
        if (!employeeId && assignableEmployees.length > 0) { toast.error("Bitte einen Mechaniker zuweisen (Pflichtfeld)"); flashMissingByTestId("wo-employee-select"); return; }
        // Jul-2026 (Bugfix „Auftrag verschwindet + Blackscreen"): Positionen mit
        // Daten aber ohne Beschreibung führten zu einem 422 — klare Meldung statt
        // Absturz; komplett leere Zeilen werden beim Senden entfernt.
        // Aug-2026 (Testing-Agent): auto-befüllter AW-Satz auf der unberührten
        // Start-Zeile zählt NICHT als Positions-Inhalt (blockierte Minimal-Anlage).
        const _hasPosData = (p) => p.article_id || Number(p.aw_count) > 0
            || (Number(p.unit_price_net) > 0 && !p._price_autofilled);
        if (positions.some((p) => !(p.description || "").trim() && _hasPosData(p))) {
            toast.error("Bitte bei allen Positionen eine Beschreibung eintragen (oder leere Zeilen entfernen).");
            return;
        }
        // Selbstannahme (24/7-Handy-Erfassung) nur mit vollständigen Pflichtdaten freigeben:
        // Kunde + Fahrzeug + Auftragswunsch müssen vorliegen.
        if (order?.source === "self_service") {
            if (!(vehicle.brand?.trim() || vehicle.model?.trim() || vehicle.vin?.trim() || vehicle.license_plate?.trim())) {
                toast.error("Selbstannahme: Bitte Fahrzeugdaten (Hersteller/Modell, FIN oder Kennzeichen) vervollständigen."); return;
            }
            if (!customerComplaint?.trim()) {
                toast.error("Selbstannahme: Bitte den Auftragswunsch des Kunden erfassen."); return;
            }
        }
        setBusy(true);
        try {
            const payload = buildPayload();
            // Leere Positions-Zeilen (ohne Beschreibung & Daten) nicht mitsenden — 422-Schutz.
            payload.positions = (payload.positions || []).filter((p) => (p.description || "").trim());
            if (!isEdit) payload.skip_confirmation = skipConfirmation;
            let savedId = order?.id;
            let created = null;
            if (isEdit) {
                await api.patch(`/work-orders/${order.id}`, payload);
            } else {
                const r = await api.post("/work-orders", payload);
                created = r.data || null;
                savedId = created?.id || savedId;
            }
            // Aug-2026 (User-Wunsch) — Termin aus der Annahme direkt einplanen:
            // Auftrag erscheint sofort im Kalender statt unter „Nicht zugeordnet".
            if (savedId && apptDate && apptTime) {
                try {
                    const sr = await api.patch(`/work-orders/${savedId}/schedule`, {
                        appointment_at: berlinWallToIso(apptDate, apptTime),
                        duration_min: Number(apptDuration) || 60,
                        resource_id: apptResource || null,
                        employee_id: employeeId || null,
                    });
                    if ((sr.data?.conflicts || []).length > 0) {
                        toast.warning(`⚠️ Termin gespeichert — Konflikt mit ${sr.data.conflicts.length} anderen Auftrag/Aufträgen (${[...new Set(sr.data.conflicts.map((c) => c.kind === "mechaniker" ? "Monteur" : "Ressource"))].join(" + ")})`, { duration: 9000 });
                    }
                } catch (se) {
                    toast.error(se?.response?.data?.detail || "Termin konnte nicht eingeplant werden — bitte im Kalender nachplanen");
                }
            } else if (isEdit && order?.appointment_at && !apptDate) {
                try { await api.delete(`/work-orders/${savedId}/schedule`); } catch { /* noop */ }
            }
            // Jul-2026 (Bugfix): Kunde ggf. org-weit nachladen (nicht immer in der
            // Liste) — damit Telefon/E-Mail beim Weiterleiten vorbefüllt sind.
            let selCust = customers.find((x) => x.id === customerId) || null;
            if (!selCust && customerId) {
                try { selCust = (await api.get(`/customers/${customerId}`)).data; } catch { selCust = null; }
            }
            // Jun-2026 — optionales Mietfahrzeug an den Auftrag buchen
            if (!isEdit && rentalEnabled && rentalVehicleId && rentalArticleId) {
                const c = selCust || {};
                const rc = {
                    first_name: c.first_name, last_name: c.last_name,
                    street: c.street, house_number: c.house_number,
                    postal_code: c.postal_code, city: c.city,
                    phone: c.phone || c.phone_mobile, email: c.email,
                };
                try {
                    await api.post("/dealer/rental-bookings", {
                        vehicle_listing_id: rentalVehicleId, article_id: rentalArticleId,
                        start_date: rentalStart, end_date: rentalEnd,
                        work_order_id: savedId,
                        customer: Object.values(rc).some(Boolean) ? rc : null,
                    });
                    toast.success("Ersatzfahrzeug reserviert");
                } catch (re) { toast.error(re.response?.data?.detail || "Ersatzfahrzeug konnte nicht gebucht werden"); }
            }
            if (isEdit) {
                toast.success("Auftrag aktualisiert");
            } else {
                // Verbindlich gespeichert → Werkstattkopie in der Vorschau anzeigen + Bestätigungs-Toast
                if (savedId) {
                    try {
                        // Jun-2026 (User-Wunsch): Druck-Bundle inkl. QR-Blatt (Seite 2) — vorher fehlte das QR-Blatt
                        const r = await api.get(`/work-orders/${savedId}/print-bundle.pdf`, { responseType: "blob" });
                        previewPdfResponse(r, `Werkstattdruck_${savedId.slice(0, 8)}.pdf`,
                            { recipient: customerRecipient(selCust) });
                    } catch (_e) { /* Vorschau optional – kein Abbruch */ }
                }
                const conf = created?.confirmation;
                let desc = "Werkstattkopie wird angezeigt.";
                if (conf?.channels) {
                    const ok = [];
                    if (conf.channels.email?.ok) ok.push("E-Mail");
                    if (conf.channels.whatsapp?.ok) ok.push("WhatsApp");
                    if (conf.channels.sms?.ok) ok.push("SMS");
                    desc = ok.length
                        ? `Werkstattkopie gedruckt · Bestätigung an Kunde: ${ok.join(", ")}`
                        : "Werkstattkopie gedruckt · Kunde ohne Kontaktdaten – keine Bestätigung versendet";
                }
                toast.success(`Auftrag ${created?.order_number || ""} gespeichert & bestätigt`,
                    { description: desc, duration: 7000 });
            }
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setBusy(false);
    };

    // Feb-2026 (User-Wunsch) — Offene Positionen in einen Folge-Auftrag auslagern.
    const [followUpOpen, setFollowUpOpen] = useState(false);
    // Aug-2026 (User-Wunsch): Auftrag teilen direkt aus der Bearbeitung
    const [shareOpen, setShareOpen] = useState(false);
    const [followUpSel, setFollowUpSel] = useState(() => new Set());
    const [followUpNote, setFollowUpNote] = useState("");
    const [followUpDate, setFollowUpDate] = useState("");
    const [followUpRemove, setFollowUpRemove] = useState(true);
    const [followUpBusy, setFollowUpBusy] = useState(false);
    const [confirmSending, setConfirmSending] = useState(false);
    const toggleFollowUpPos = (i) => setFollowUpSel((s) => {
        const n = new Set(s);
        if (n.has(i)) n.delete(i); else n.add(i);
        return n;
    });

    const [printBusy, setPrintBusy] = useState(false);


    // Auto-Speichern der aktuellen Eingaben, damit der „Auftrag aktualisieren"-
    // Button bei Folgeaktionen (Drucken/Unterschrift) entbehrlich ist.
    const persist = async () => {
        if (!isEdit) return;
        if (positions.some((p) => !p.description?.trim())) return; // unvollständig → nicht speichern
        try { await api.patch(`/work-orders/${order.id}`, buildPayload()); } catch { /* best-effort */ }
    };

    // Jul-2026 (User-Wunsch) — AUTOSPEICHERN bei Bearbeitung: Änderungen werden
    // 1,5 s nach der letzten Eingabe automatisch gespeichert (still, ohne Toast).
    const [autoSavedAt, setAutoSavedAt] = useState(null);
    const autosaveReady = useRef(false);
    const payloadJson = isEdit ? JSON.stringify(buildPayload()) : "";
    useEffect(() => {
        if (!isEdit) return undefined;
        if (!autosaveReady.current) { autosaveReady.current = true; return undefined; }
        const h = setTimeout(async () => {
            if (positions.some((p) => !p.description?.trim())) return; // unvollständig
            if (!customerComplaint?.trim()) return; // Pflichtfeld leer → nicht überschreiben
            try {
                await api.patch(`/work-orders/${order.id}`, buildPayload());
                setAutoSavedAt(new Date());
            } catch { /* still — manuelles Speichern bleibt möglich */ }
        }, 1500);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payloadJson, isEdit]);

    const handlePrintBundle = async () => {
        if (!isEdit) { toast.error("Bitte den Auftrag zuerst speichern."); return; }
        setPrintBusy(true);
        try {
            await persist();
            const r = await api.get(`/work-orders/${order.id}/print-bundle.pdf`, { responseType: "blob" });
            // autoPrint: Druckdialog direkt öffnen (Vorschau bleibt im Hintergrund).
            previewPdfResponse(r, `Werkstattdruck_${order.order_number || order.id.slice(0, 8)}.pdf`, { autoPrint: true });
        } catch {
            toast.error("Druck-Bundle konnte nicht erstellt werden.");
        } finally {
            setPrintBusy(false);
        }
    };



    const createFollowUp = async () => {
        if (!isEdit) { toast.error("Bitte den Auftrag zuerst speichern."); return; }
        if (positions.some((p) => !p.description?.trim())) { toast.error("Alle Positionen brauchen eine Beschreibung"); return; }
        const idxs = [...followUpSel].sort((a, b) => a - b);
        if (idxs.length === 0) { toast.error("Bitte mindestens eine Position für den Folge-Auftrag wählen"); return; }
        setFollowUpBusy(true);
        try {
            // Erst speichern, damit DB-Positionen mit der Auswahl übereinstimmen.
            await api.patch(`/work-orders/${order.id}`, buildPayload());
            const r = await api.post(`/work-orders/${order.id}/split-follow-up`, {
                position_indices: idxs,
                note: followUpNote || null,
                appointment_at: followUpDate ? `${followUpDate}T09:00:00` : null,
                remove_from_source: followUpRemove,
            });
            toast.success(`Folge-Auftrag ${r.data.follow_up.order_number} erstellt (${idxs.length} Position${idxs.length !== 1 ? "en" : ""}).`, { duration: 6000 });
            onSaved();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Folge-Auftrag fehlgeschlagen");
        } finally { setFollowUpBusy(false); }
    };

    // Jun-2026 (User-Wunsch) — Termin-Quick-Actions (aus Push-Deep-Link / Modal):
    // Wunschtermin als erledigt markieren oder um X Tage verschieben (mit
    // Pflicht-Grund → Kundendienst-Freigabe, keine direkte Kundeninfo).
    const [termBusy, setTermBusy] = useState(false);
    const [showPostpone, setShowPostpone] = useState(false);
    const [postponeDays, setPostponeDays] = useState(3);
    const [postponeReason, setPostponeReason] = useState("");
    const termDate = (order?.estimated_completion || "").slice(0, 10);
    const isTermOverdue = termDate && termDate < new Date().toISOString().slice(0, 10);
    const resolveTerm = async () => {
        setTermBusy(true);
        try {
            await api.post(`/work-orders/${order.id}/completion/resolve`);
            toast.success("Wunschtermin als erledigt markiert");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        finally { setTermBusy(false); }
    };
    const postponeTerm = async () => {
        if (!postponeReason.trim()) { toast.error("Bitte einen Grund für die Verschiebung angeben"); return; }
        setTermBusy(true);
        try {
            await api.post(`/work-orders/${order.id}/completion/postpone`,
                { days: postponeDays, reason: postponeReason.trim() });
            toast.success(`Wunschtermin um ${postponeDays} Tag${postponeDays !== 1 ? "e" : ""} verschoben`,
                { description: "Kundendienst wurde zur Freigabe der Kundeninfo benachrichtigt." });
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        finally { setTermBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="wo-modal">
            <div className="bg-white rounded-sm w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                        {/* Jul-2026 (User-Wunsch): Zurück-Button zur Auftragserfassung. */}
                        {!isEdit && onBack && (
                            <button type="button" onClick={onBack}
                                className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-gray-900 border border-gray-300 rounded-sm px-2.5 py-1.5"
                                data-testid="wo-back-btn">
                                ← Zurück
                            </button>
                        )}
                        <h2 className="font-display font-black text-xl">
                            {isEdit ? `Auftrag ${order.order_number}` : "Neuer Werkstatt-Auftrag"}
                            {isEdit && order?.internal_vehicle_listing_id && !order?.customer_id && (
                                <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 rounded-sm px-1.5 py-0.5" data-testid="wo-internal-badge">
                                    Interner Auftrag · auf Fahrzeug gebucht
                                </span>
                            )}
                        </h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="modal-close">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    {/* Jun-2026 (User-Wunsch) — 24/7-Selbstannahme: vom Kunden per Handy erfasst. */}
                    {order?.source === "self_service" && (
                        <div className="rounded-sm p-3 bg-[#111827] text-white" data-testid="wo-self-service-banner">
                            <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] flex items-center gap-1.5">
                                <Camera className="w-4 h-4" /> 24/7-Selbstannahme · bitte prüfen &amp; freigeben
                            </div>
                            {order?.appointment_wish && (
                                <div className="text-sm mt-1.5">Wunschtermin (Kunde): <b>{order.appointment_wish}</b></div>
                            )}
                            <div className="text-[11px] text-white/60 mt-0.5">
                                Angaben & Belege wurden vom Kunden am Handy erfasst. Kontaktdaten ggf. abgleichen.
                            </div>
                        </div>
                    )}

                    {/* Jun-2026 (User-Wunsch) — Termin-Quick-Actions: erledigt / verschieben.
                        Sichtbar bei bestehendem Auftrag mit gesetztem Wunschtermin. */}
                    {isEdit && termDate && !order?.completion_resolved && (
                        <div className={`rounded-sm p-3 border-l-4 ${isTermOverdue ? "bg-red-50 border-red-500" : "bg-amber-50 border-amber-500"}`} data-testid="wo-term-quickactions">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className={`text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${isTermOverdue ? "text-red-900" : "text-amber-900"}`}>
                                    <CalendarClock className="w-4 h-4" />
                                    Wunschtermin {termDate.split("-").reverse().join(".")}{isTermOverdue ? " · überfällig" : ""}
                                </span>
                                <div className="flex flex-wrap items-center gap-1.5 ml-auto">
                                    <Button type="button" size="sm" disabled={termBusy} onClick={resolveTerm}
                                        className="h-8 text-[11px] rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wider"
                                        data-testid="wo-term-resolve">
                                        ✓ Erledigt
                                    </Button>
                                    <Button type="button" size="sm" variant="outline" disabled={termBusy}
                                        onClick={() => setShowPostpone((v) => !v)}
                                        className="h-8 text-[11px] rounded-sm font-bold"
                                        data-testid="wo-term-postpone-toggle">
                                        Verschieben
                                    </Button>
                                </div>
                            </div>
                            {showPostpone && (
                                <div className="mt-3 border-t border-black/10 pt-3 space-y-2" data-testid="wo-term-postpone-panel">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Um:</span>
                                        {[1, 3, 7].map((d) => (
                                            <button key={d} type="button" onClick={() => setPostponeDays(d)}
                                                className={`h-7 px-2.5 rounded-sm text-[11px] font-bold border ${postponeDays === d ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300"}`}
                                                data-testid={`wo-term-postpone-${d}`}>
                                                +{d} {d === 1 ? "Tag" : "Tage"}
                                            </button>
                                        ))}
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Grund (Pflicht) — geht an den Kundendienst zur Freigabe</label>
                                        <Textarea value={postponeReason} onChange={(e) => setPostponeReason(e.target.value)}
                                            rows={2} placeholder="z. B. Ersatzteil verzögert sich, Lieferung erst nächste Woche"
                                            className="rounded-sm mt-0.5 text-sm bg-white" data-testid="wo-term-postpone-reason" />
                                    </div>
                                    <Button type="button" size="sm" disabled={termBusy || !postponeReason.trim()} onClick={postponeTerm}
                                        className="h-8 text-[11px] rounded-sm bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider"
                                        data-testid="wo-term-postpone-submit">
                                        Verschieben & Kundendienst informieren
                                    </Button>
                                    <p className="text-[10px] text-gray-500">Es geht <strong>keine</strong> direkte Kundeninfo raus — der Kundendienst gibt sie frei.</p>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Aug-2026 (User-Wunsch): BEREICH 1 — Kunde & Fahrzeug (normales Design),
                        auf dem Werkstatt-Tablet einklappbar. */}
                    <div>
                    <button type="button" onClick={() => setSecOpen((o) => ({ ...o, kunde: !o.kunde }))}
                        className="lg:hidden w-full flex items-center justify-between bg-gray-100 border border-gray-200 rounded-sm px-3 py-2 mb-2"
                        data-testid="wo-sec-toggle-kunde">
                        <span className="text-xs font-black uppercase tracking-wider text-gray-700">Kunde &amp; Fahrzeug</span>
                        <ChevronsUpDown className="w-4 h-4 text-gray-500" />
                    </button>
                    <div className={`${secOpen.kunde ? "" : "hidden"} lg:block space-y-3`}>
                    <WoCustomerPicker value={customerId} customers={customers} onChange={setCustomerId} />

                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-2" data-testid="wo-vehicle">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
                            <Car className="w-3.5 h-3.5" /> Fahrzeug
                        </div>
                        {(garage.length > 0 || vkCheck) && (
                        <div className="flex flex-wrap items-start justify-between gap-2">
                        {garage.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 min-w-0" data-testid="wo-garage-picker">
                                {garage.map((v) => {
                                    const sel = selectedVehicleId === v.id;
                                    return (
                                        <button
                                            key={v.id}
                                            type="button"
                                            onClick={() => pickGarageVehicle(v)}
                                            className={`px-2 py-1 rounded-sm text-[11px] font-bold border ${sel ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"}`}
                                            data-testid={`wo-garage-vehicle-${v.id}`}
                                        >
                                            {(v.brand || "")} {(v.model || "")}{plateLabel(v) ? ` · ${plateLabel(v)}` : ""}
                                        </button>
                                    );
                                })}
                                <button
                                    type="button"
                                    onClick={newGarageVehicle}
                                    className={`px-2 py-1 rounded-sm text-[11px] font-bold border ${selectedVehicleId === null ? "bg-[#FFCC00]/20 border-[#FFCC00]" : "bg-white text-gray-600 border-dashed border-gray-400 hover:border-gray-600"}`}
                                    data-testid="wo-garage-new"
                                >
                                    + Neues Fahrzeug
                                </button>
                            </div>
                        )}
                        {/* Aug-2026 (User-Wunsch): Plausibilitäts-Hinweis rechts neben der Garage-Zeile */}
                        {vkCheck && (
                            <div className={`text-[11px] rounded-sm px-2.5 py-1.5 border max-w-md ${vkCheck.mismatch ? "text-red-800 bg-red-50 border-red-300" : "text-emerald-800 bg-emerald-50 border-emerald-300"}`} data-testid="wo-vk-hint">
                                {vkCheck.mismatch
                                    ? `⚠ Plausibilität: Wissensdatenbank erwartet für diese FIN „${vkCheck.manufacturer}${vkCheck.model ? ` ${vkCheck.model}` : ""}" — bitte Marke/FIN prüfen.`
                                    : `✓ FIN plausibel: ${[vkCheck.manufacturer, vkCheck.model, vkCheck.model_year].filter(Boolean).join(" · ")}${vkCheck.verified ? " (verifiziert)" : " (KI-gelernt — bitte prüfen)"}`}
                            </div>
                        )}
                        </div>
                        )}
                        {/* Jun-2026 (User-Wunsch): Warnung NUR bei Leasing — Finanzierungs-Hinweis entfernt. */}

                        {(() => {
                            const dups = vinDup.filter((m) => !customerId || m.customer_id !== customerId);
                            return dups.length > 0 ? (
                                <div className="mb-2 text-[11px] rounded-sm px-2.5 py-1.5 border border-amber-300 bg-amber-50 text-amber-900" data-testid="wo-vin-duplicate-warning">
                                    <div className="font-bold mb-0.5">⚠ FIN bereits an einem Standort vorhanden:</div>
                                    <ul className="space-y-0.5">
                                        {dups.slice(0, 3).map((m) => (
                                            <li key={m.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => { onClose?.(); navigate(`/dealer/invoices?tab=customers&q=${encodeURIComponent(m.customer_name || "")}`); }}
                                                    className="text-left underline decoration-dotted hover:text-amber-950 truncate w-full"
                                                    title="Zum bestehenden Kunden springen"
                                                    data-testid={`wo-vin-dup-jump-${m.id}`}
                                                >
                                                    {(m.brand || "")} {(m.model || "")} — Kunde {m.customer_name}{m.site ? ` · Standort ${m.site}` : ""} →
                                                </button>
                                            </li>
                                        ))}
                                        {dups.length > 3 && <li>… und {dups.length - 3} weitere</li>}
                                    </ul>
                                </div>
                            ) : null;
                        })()}
                        {vinHistory.length > 0 && (
                            <div className="mb-2 text-[11px] rounded-sm px-2.5 py-1.5 border border-blue-200 bg-blue-50 text-blue-900" data-testid="wo-vin-history">
                                <div className="font-bold mb-1">Frühere Aufträge zu diesem Fahrzeug ({vinHistory.length}):</div>
                                <ul className="space-y-0.5">
                                    {vinHistory.map((h) => (
                                        <li key={h.id} className="truncate" data-testid={`wo-vin-history-${h.id}`}>
                                            {h.created_at ? new Date(h.created_at).toLocaleDateString("de-DE") : "—"} · {h.order_number || "—"} · {({ open: "Offen", in_progress: "In Arbeit", done: "Fertig", invoiced: "Abgerechnet", cancelled: "Storniert" }[h.status] || h.status)}{h.mileage ? ` · ${h.mileage} km` : ""}{h.summary ? ` — ${h.summary}` : ""}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {financeStatus && (financeStatus.financing || financeStatus.leasing) && (
                            <div className="mb-2 space-y-1.5" data-testid="wo-finance-status">
                                {financeStatus.leasing && (
                                    <div className={`text-[11px] rounded-sm px-2.5 py-1.5 border flex flex-wrap items-center gap-x-2 gap-y-0.5 ${financeStatus.leasing.expired ? "border-amber-300 bg-amber-50 text-amber-900" : "border-purple-200 bg-purple-50 text-purple-900"}`} data-testid="wo-finance-leasing">
                                        <span className="font-black uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-purple-600 text-white text-[10px]">Leasing</span>
                                        <span className="font-bold">{financeStatus.leasing.leasing_company || "Leasingfahrzeug"}</span>
                                        {financeStatus.leasing.contract_number && <span>· Vertrag {financeStatus.leasing.contract_number}</span>}
                                        {financeStatus.leasing.end_date && (
                                            <span className="font-bold">· {financeStatus.leasing.expired ? "ausgelaufen am" : "Vertragsende"} {new Date(financeStatus.leasing.end_date).toLocaleDateString("de-DE")}</span>
                                        )}
                                        {typeof financeStatus.leasing.residual_value === "number" && financeStatus.leasing.residual_value > 0 && (
                                            <span>· Restwert {financeStatus.leasing.residual_value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                                        )}
                                    </div>
                                )}
                                {financeStatus.financing && (
                                    <div className={`text-[11px] rounded-sm px-2.5 py-1.5 border flex flex-wrap items-center gap-x-2 gap-y-0.5 ${financeStatus.financing.expired ? "border-amber-300 bg-amber-50 text-amber-900" : "border-blue-200 bg-blue-50 text-blue-900"}`} data-testid="wo-finance-financing">
                                        <span className="font-black uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-blue-600 text-white text-[10px]">Finanziert</span>
                                        <span className="font-bold">{financeStatus.financing.bank || "Finanzierung"}</span>
                                        {financeStatus.financing.contract_number && <span>· Vertrag {financeStatus.financing.contract_number}</span>}
                                        {financeStatus.financing.end_date && (
                                            <span className="font-bold">· {financeStatus.financing.expired ? "ausgelaufen am" : "Laufzeitende"} {new Date(financeStatus.financing.end_date).toLocaleDateString("de-DE")}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <BrandCombobox
                                value={vehicle.brand || ""}
                                onValueChange={(v) => setVeh("brand", v)}
                                groups={WO_BRAND_GROUPS}
                                placeholder="Marke wählen oder eingeben…"
                                searchPlaceholder="Hersteller suchen oder eingeben…"
                                testId="wo-veh-brand"
                                triggerClassName="h-10 rounded-sm"
                                allowCustom
                            />
                            <Input placeholder="Modell" value={vehicle.model || ""} onChange={(e) => setVeh("model", e.target.value)} className="rounded-sm" data-testid="wo-veh-model" />
                            {/* Jul-2026 (User-Wunsch): Überschrift entfernt — Platzhalter im Feld. */}
                            <Input
                                type={vehicle.first_registration ? "date" : "text"}
                                placeholder="Erstzulassung"
                                onFocus={(e) => { e.target.type = "date"; }}
                                onBlur={(e) => { if (!e.target.value) e.target.type = "text"; }}
                                value={(vehicle.first_registration || "").slice(0, 10)}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setVehicle((x) => ({ ...x, first_registration: v, year: v ? v.slice(0, 4) : x.year }));
                                }}
                                max={new Date().toISOString().slice(0, 10)}
                                className="rounded-sm"
                                data-testid="wo-veh-first-registration"
                            />
                            <Input placeholder="Kennzeichen" value={vehicle.license_plate || ""} onChange={(e) => setVeh("license_plate", e.target.value)} className="rounded-sm font-mono-num" data-testid="wo-veh-plate" />
                            <div className="col-span-2">
                                <Input placeholder="FIN/VIN" value={vehicle.vin || ""} onChange={(e) => setVeh("vin", e.target.value)} className="rounded-sm font-mono-num" data-testid="wo-veh-vin" />
                                <VinWarning value={vehicle.vin} />
                            </div>
                            <Input type="number" min="0" placeholder="Laufleistung km" value={vehicle.mileage || ""} onChange={(e) => setVeh("mileage", e.target.value)} className="rounded-sm border-amber-400 bg-amber-50/60 focus:border-amber-500" data-testid="wo-veh-mileage" />
                            <Input placeholder="Farbe" value={vehicle.color || ""} onChange={(e) => setVeh("color", e.target.value)} className="rounded-sm" />
                            {/* Aug-2026 (User-Wunsch): Nächste HU + Abstellort + Wunschtermin in EINER Zeile, Überschriften als Platzhalter */}
                            <div className="col-span-2 md:col-span-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <Input
                                    type={tuevFocused || (vehicle.tuev_until || "") ? "month" : "text"}
                                    placeholder="Nächste HU (Monat/Jahr)"
                                    onFocus={() => setTuevFocused(true)}
                                    onBlur={() => setTuevFocused(false)}
                                    value={(vehicle.tuev_until || "").slice(0, 7)}
                                    onChange={(e) => setVeh("tuev_until", e.target.value)}
                                    className="rounded-sm" data-testid="wo-veh-tuev" />
                                <Input
                                    placeholder="Abstellort (z.B. Hof Platz 12)"
                                    value={parkingSpot}
                                    onChange={(e) => setParkingSpot(e.target.value)}
                                    className="rounded-sm border-amber-400 bg-amber-50/60 focus:border-amber-500"
                                    data-testid="wo-parking-spot"
                                    maxLength={80}
                                />
                                <Input
                                    type={wishFocused || estimatedCompletion ? "date" : "text"}
                                    placeholder={order?.order_type === "delivery_internal" ? "Auslieferungstermin (= Wunschtermin)" : "Wunschtermin Fertigstellung"}
                                    onFocus={() => setWishFocused(true)}
                                    onBlur={() => setWishFocused(false)}
                                    value={(estimatedCompletion || "").slice(0, 10)}
                                    onChange={(e) => setEstimatedCompletion(e.target.value)}
                                    className="rounded-sm border-amber-400 bg-amber-50/60 focus:border-amber-500"
                                    data-testid="wo-estimated-completion"
                                />
                            </div>
                            {hasWinterCheckin && (
                                <div className="col-span-2 rounded-sm p-3 -m-0.5 ring-2 ring-sky-300 bg-sky-50/70" data-testid="wo-winter-storage-box">
                                    <div className="text-[11px] font-black uppercase tracking-wider text-sky-800 mb-2">❄ Winterlager · Zeitraum</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <label className="block">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Einlagerdatum</span>
                                            <Input
                                                type="date"
                                                value={winterInDate}
                                                onChange={(e) => setWinterInDate(e.target.value)}
                                                className="rounded-sm h-9 mt-0.5"
                                                data-testid="wo-winter-in-date"
                                            />
                                        </label>
                                        <label className="block">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Geplantes Auslagerdatum <span className="font-normal normal-case text-sky-500">(voraussichtlich)</span></span>
                                            <Input
                                                type="date"
                                                value={winterPlannedOut}
                                                min={winterInDate || undefined}
                                                onChange={(e) => setWinterPlannedOut(e.target.value)}
                                                className="rounded-sm h-9 mt-0.5"
                                                data-testid="wo-winter-planned-out-date"
                                            />
                                        </label>
                                    </div>
                                    <p className="text-[10px] text-sky-700 mt-1.5">Wird beim Auftrags-Abschluss ins Winterlager übernommen. Das geplante Auslagerdatum erscheint dort als Erinnerung.</p>
                                </div>
                            )}
                            {/* Jul-2026 (User-Wunsch): Wunschtermin in die HU/Abstellort-Zeile verschoben (Aug-2026). */}
                            {/* Aug-2026 (User-Wunsch) — fester Termin: erst per Häkchen ausklappen, gelbe Umrandung */}
                            {/* Aug-2026 (User-Wunsch): Kalender-Erfassung in den gelben Mechaniker-Bereich verschoben */}
                            {/* Aug-2026 (User-Wunsch): archivierte Unterlagen rechts neben dem Termin-Block */}
                            {documents.length > 0 && (
                                <div className="col-span-2 bg-white border border-gray-200 rounded-sm p-2 self-start" data-testid="wo-documents">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Am Fahrzeug archivierte Unterlagen</div>
                                    <ul className="space-y-1">
                                        {documents.map((d, i) => (
                                            <li key={i} className="flex items-center justify-between text-xs">
                                                <span className="truncate text-gray-700">{(d.kind || "Dok").toUpperCase()} · {d.name || "Unterlage"}</span>
                                                {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-bold shrink-0 ml-2" data-testid={`wo-document-${i}`}>Ansehen</a>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        {order?.is_quick_capture && (
                            <div className="mt-2 bg-amber-50 border-l-4 border-amber-500 px-3 py-2 text-[11px] text-amber-900 rounded-sm" data-testid="wo-quick-banner">
                                <strong>Schnell-Erfassung:</strong> Bitte ergänze die fehlenden Stammdaten (Adresse, FIN, Kennzeichen, Laufleistung) vor der Rechnungsstellung.
                            </div>
                        )}
                    </div>

                    </div>
                    </div>

                    {/* Aug-2026 (User-Wunsch): BEREICH 2 — Auftragsdaten (gelb):
                        Mechaniker, Kalender, Kundenauftrag + Hinweise zu anstehenden Arbeiten. */}
                    <div className="rounded-sm ring-2 ring-amber-300 bg-amber-50/60 p-3 space-y-2" data-testid="wo-orderdata-section">
                    <button type="button" onClick={() => setSecOpen((o) => ({ ...o, daten: !o.daten }))}
                        className="w-full flex items-center justify-between text-left lg:pointer-events-none"
                        data-testid="wo-sec-toggle-daten">
                        <span className="text-xs font-black uppercase tracking-wider text-amber-900 inline-flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5" /> Auftragsdaten
                        </span>
                        <ChevronsUpDown className="w-4 h-4 text-amber-700 lg:hidden" />
                    </button>
                    <div className={`${secOpen.daten ? "" : "hidden"} lg:block space-y-2`}>
                    <div className="space-y-2">
                            {assignableEmployees.length > 0 ? (
                                <select
                                    value={employeeId}
                                    onChange={(e) => {
                                        setEmployeeId(e.target.value);
                                        const sel = assignableEmployees.find((emp) => emp.id === e.target.value);
                                        if (sel) {
                                            const parts = [sel.name];
                                            if (sel.employee_function) parts.push(`(${sel.employee_function}${sel.mechanic_specialty ? " · " + sel.mechanic_specialty : ""})`);
                                            setEmployee(parts.join(" "));
                                            // Aug-2026 (User-Wunsch): dem Mechaniker zugeordnete Ressource
                                            // vorsorglich MIT auswählen — klappt den Kalender NICHT auf.
                                            const res = calResources.find((r) => r.mechanic_id === sel.id)
                                                || calResources.find((r) => {
                                                    const fn = String(sel.name || "").trim().split(/\s+/)[0];
                                                    return fn.length >= 3 && String(r.name || "").toLowerCase().includes(fn.toLowerCase());
                                                });
                                            if (res) setApptResource(res.id);
                                        } else {
                                            setEmployee("");
                                        }
                                    }}
                                    className="w-full rounded-sm border border-amber-300 px-3 py-2 text-sm bg-white"
                                    data-testid="wo-employee-select"
                                >
                                    <option value="">Mechaniker zuweisen *</option>
                                    {assignableEmployees.map((emp) => (
                                        <option key={emp.id} value={emp.id}>
                                            {emp.name}
                                            {emp.employee_function ? ` · ${emp.employee_function}` : ""}
                                            {emp.mechanic_specialty ? ` (${emp.mechanic_specialty})` : ""}
                                            {emp.has_login === false ? " · ohne Login" : ""}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <Input value={employee} onChange={(e) => setEmployee(e.target.value)} className="rounded-sm border-amber-300" placeholder="Mechaniker zuweisen * — z. B. Hans Mustermann" data-testid="wo-employee-text" />
                            )}
                            {/* Aug-2026 (User-Wunsch): Kalender-Erfassung im selben gelben Bereich wie der Mechaniker */}
                            <div data-testid="wo-appointment-box">
                                <label className="flex items-center gap-2 cursor-pointer text-[11px] font-black uppercase tracking-wider text-amber-800">
                                    <input type="checkbox" checked={apptOpen}
                                        onChange={(e) => { setApptOpen(e.target.checked); if (!e.target.checked) { setApptDate(""); setApptTime(""); } }}
                                        className="accent-amber-500" data-testid="wo-appt-toggle" />
                                    📅 Gleich im Kalender erfassen?
                                    <InfoHint id="workshop.appointment_info" defaultText="Mit Datum + Uhrzeit wird der Auftrag beim Speichern direkt im Werkstatt-Kalender eingeplant (Monteur = zugewiesener Mechaniker) und zählt für Auslastung und Online-Buchung." side="bottom" />
                                </label>
                                {apptOpen && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                                    <Input type={apptDate ? "date" : "text"} placeholder="Datum" title="Datum"
                                        onFocus={(e) => { e.target.type = "date"; }}
                                        onBlur={(e) => { if (!e.target.value) e.target.type = "text"; }}
                                        value={apptDate} onChange={(e) => setApptDate(e.target.value)}
                                        className="rounded-sm h-9 bg-white" data-testid="wo-appt-date" />
                                    <Input type={apptTime ? "time" : "text"} placeholder="Uhrzeit" title="Uhrzeit"
                                        onFocus={(e) => { e.target.type = "time"; }}
                                        onBlur={(e) => { if (!e.target.value) e.target.type = "text"; }}
                                        value={apptTime} onChange={(e) => setApptTime(e.target.value)}
                                        className="rounded-sm h-9 bg-white" data-testid="wo-appt-time" />
                                    <select value={apptDuration} onChange={(e) => setApptDuration(e.target.value)} title="Dauer"
                                        className="w-full rounded-sm h-9 bg-white border border-input px-2 text-sm" data-testid="wo-appt-duration">
                                        {[30, 60, 90, ...Array.from({ length: 13 }, (_, i) => 120 + i * 30)].map((m) => (
                                            <option key={m} value={m}>Dauer: {m < 60 ? `${m} Min` : `${(m / 60).toLocaleString("de-DE")} Std`}</option>
                                        ))}
                                    </select>
                                    <select value={apptResource} onChange={(e) => setApptResource(e.target.value)} title="Ressource"
                                        className="w-full rounded-sm h-9 bg-white border border-input px-2 text-sm" data-testid="wo-appt-resource">
                                        <option value="">Ressource — optional</option>
                                        {calResources.map((res) => (
                                            <option key={res.id} value={res.id}>{res.name}</option>
                                        ))}
                                    </select>
                                </div>
                                )}
                                {apptOpen && apptConflicts.length > 0 && (
                                    <div className="mt-2 rounded-sm border border-amber-400 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900 font-bold" data-testid="wo-appt-conflict-warning">
                                        ⚠️ Terminkonflikt: {apptConflicts.map((c) => `${c.kind} bereits verplant (${c.order_number || "Auftrag"} · ${c.time} Uhr)`).join(" · ")} — Speichern ist trotzdem möglich.
                                    </div>
                                )}
                            </div>
                        </div>
                        {usageInfo?.leasing && (
                            <div className="mb-2 text-[11px] rounded-sm px-2.5 py-1.5 border border-amber-400 bg-amber-50 text-amber-900" data-testid="wo-leasing-hint">
                                <div className="font-black uppercase tracking-wider mb-0.5">
                                    Leasingfahrzeug{usageInfo.leasing.leasing_company ? ` · ${usageInfo.leasing.leasing_company}` : ""}{usageInfo.leasing.contract_end ? ` · Vertrag bis ${new Date(usageInfo.leasing.contract_end).toLocaleDateString("de-DE")}` : ""}
                                </div>
                                Fahrzeug auf <b>Beschädigungen</b> und <b>rückständige Wartungen</b> prüfen — diese müssen nachgeholt werden. Zum Werterhalt für den Wiederverkauf für die Leasinggesellschaft.
                            </div>
                        )}

                    {/* Jun-2026 (User-Wunsch) — HU/TÜV-Vorschlag bei fälliger Hauptuntersuchung */}
                    {(() => {
                        const selVeh = (garage || []).find((g) => g.id === selectedVehicleId) || null;
                        const huDue = computeHuDue(vehicle) || computeHuDue(selVeh);
                        // Aug-2026 (Testing-Fix): Positionen, die nur den Kundenauftrags-Text
                        // spiegeln (_desc_from_complaint), unterdrücken den Banner NICHT —
                        // nur echte HU/TÜV-Positionen (z. B. „HU/TÜV-Vorbereitung") zählen.
                        const already = positions.some((p) => !p._desc_from_complaint && /hu|tüv|tuev/i.test(p.description || ""));
                        if (!huDue || huDue.state === "ok" || already) return null;
                        return (
                            <div className={`rounded-sm px-3 py-2 flex items-center justify-between gap-3 border ${huDue.state === "overdue" ? "bg-red-50 border-red-300" : "bg-amber-50 border-amber-300"}`} data-testid="wo-hu-suggestion">
                                <div className={`text-xs flex items-center gap-2 ${huDue.state === "overdue" ? "text-red-900" : "text-amber-900"}`}>
                                    <CalendarClock className={`w-4 h-4 shrink-0 ${huDue.state === "overdue" ? "text-red-600" : "text-amber-600"}`} />
                                    <span>HU/TÜV <b>{huDue.label}</b> — Position „HU/TÜV-Vorbereitung“ zum Auftrag hinzufügen?</span>
                                </div>
                                <Button type="button" size="sm" onClick={addHuPosition} className={`h-7 text-[11px] rounded-sm font-bold text-white shrink-0 ${huDue.state === "overdue" ? "bg-red-500 hover:bg-red-600" : "bg-amber-500 hover:bg-amber-600"}`} data-testid="wo-hu-add">
                                    + Übernehmen
                                </Button>
                            </div>
                        );
                    })()}

                    {/* Jul-2026 (User-Wunsch): Überschrift entfernt — Wert kommt aus der
                        Auftragserfassung (vorheriges Popup) und ist hier vorbefüllt. */}
                    <div className={`grid grid-cols-1 gap-3 items-start ${maintDue?.due?.length > 0 ? "md:grid-cols-3" : ""}`}>
                        <div className={maintDue?.due?.length > 0 ? "md:col-span-2" : ""}>
                            {/* Aug-2026 (User-Wunsch): doppelten HU-Hinweis entfernt — es gilt nur
                                noch der Banner mit „+ Übernehmen" oberhalb (wo-hu-suggestion). */}
                            {/* Aug-2026 (User-Wunsch): Label „Kundenauftrag *" ins Eingabefeld */}
                            <Textarea value={customerComplaint} onChange={(e) => setCustomerComplaint(e.target.value)} className="rounded-sm min-h-[50px] border-amber-300 bg-white" placeholder="Kundenauftrag * (im Wortlaut) — Pflichtfeld" data-testid="wo-complaint" />
                        </div>
                        {/* Aug-2026 (User-Wunsch): Wartungsplan rechts neben dem Kundenauftrag (1/3 der Zeile) */}
                        {maintDue?.due?.length > 0 && (
                            <div className="text-[11px] rounded-sm px-2.5 py-1.5 border border-purple-300 bg-purple-50 text-purple-900" data-testid="wo-maintenance-hint">
                                <div className="font-bold mb-0.5">Wartungsplan {maintDue.plan.manufacturer} {maintDue.plan.model}</div>
                                <ul className="list-disc ml-4 space-y-0.5">
                                    {maintDue.due.slice(0, 5).map((d, i) => (
                                        <li key={i}>{d.title}{d.due_at_km ? ` (ab ${Number(d.due_at_km).toLocaleString("de-DE")} km)` : d.due_at_months ? ` (ab ${d.due_at_months} Monaten)` : ""}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    </div>
                    </div>

                    {/* Jun-2026 (User-Wunsch) — Ersatz-/Mietfahrzeug zum Auftrag buchen */}
                    {!isEdit && (
                        <div className="bg-teal-900 border border-teal-400 rounded-sm p-3 space-y-2" data-testid="wo-rental-block">
                            <div className="flex items-center justify-between gap-2">
                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={rentalEnabled} onChange={(e) => setRentalEnabled(e.target.checked)} data-testid="wo-rental-toggle" />
                                    <span className="text-xs font-bold uppercase tracking-wider text-white">Ersatz-/Mietfahrzeug zuweisen</span>
                                </label>
                                <Button type="button" size="sm" onClick={() => setShowRecommend(true)}
                                    className="h-8 text-xs rounded-sm bg-teal-400 hover:bg-teal-300 text-teal-950 font-bold" data-testid="wo-recommend-partner-btn">
                                    <Share2 className="w-3.5 h-3.5 mr-1" /> Partner empfehlen
                                </Button>
                            </div>
                            {rentalEnabled && (
                                rentalVehicles.length === 0 ? (
                                    <p className="text-[11px] text-teal-100" data-testid="wo-rental-none">Aktuell kein verfügbares Miet-/Werkstattersatzfahrzeug.</p>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        <div className="md:col-span-2">
                                            <Select value={rentalVehicleId} onValueChange={setRentalVehicleId}>
                                                <SelectTrigger className="rounded-sm h-9 bg-white" data-testid="wo-rental-vehicle"><SelectValue placeholder="Fahrzeug wählen…" /></SelectTrigger>
                                                <SelectContent>
                                                    {rentalVehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.manufacturer} {v.model} · {v.license_plate || "ohne Kennz."}{v.displacement ? ` · ${v.displacement} ccm` : ""}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="md:col-span-2">
                                            <Select value={rentalArticleId} onValueChange={setRentalArticleId}>
                                                <SelectTrigger className="rounded-sm h-9 bg-white" data-testid="wo-rental-article"><SelectValue placeholder="Tarif (Tagespreis)…" /></SelectTrigger>
                                                <SelectContent>
                                                    {rentalArticles.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} — {Number(a.price_per_day_gross || 0).toFixed(2)} €/Tag</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <Input type="date" value={rentalStart} onChange={(e) => setRentalStart(e.target.value)} className="rounded-sm h-9 bg-white" data-testid="wo-rental-start" />
                                        <Input type="date" value={rentalEnd} min={rentalStart} onChange={(e) => setRentalEnd(e.target.value)} className="rounded-sm h-9 bg-white" data-testid="wo-rental-end" />
                                    </div>
                                )
                            )}
                        </div>
                    )}

                    {/* Positionen — Jul-2026 (User-Wunsch): Vorschläge + Liste zuerst,
                        die Hinzufüge-Leiste (Titel/Kamera/Freie Position/WWS-Suche/EAN)
                        steht UNTER der Liste, direkt über Interner Notiz & Summen. */}
                    {/* Aug-2026 (User-Wunsch): BEREICH 3 — Auftragsbearbeitung Lohn & Teile (blau) */}
                    <div className="rounded-sm ring-2 ring-blue-300 bg-blue-50/40 p-3" data-testid="wo-positions-section">
                        <button type="button" onClick={() => setSecOpen((o) => ({ ...o, teile: !o.teile }))}
                            className="w-full flex items-center justify-between text-left lg:pointer-events-none mb-2"
                            data-testid="wo-sec-toggle-teile">
                            <span className="text-xs font-black uppercase tracking-wider text-blue-900 inline-flex items-center gap-1.5">
                                <Wrench className="w-3.5 h-3.5" /> Auftragsbearbeitung — Lohn &amp; Teile
                            </span>
                            <ChevronsUpDown className="w-4 h-4 text-blue-700 lg:hidden" />
                        </button>
                        <div className={`${secOpen.teile ? "" : "hidden"} lg:block`}>
                        {/* · 265 — Auftrags-Presets: Schnell-Chips, Häufig genutzt, Vorlagen */}
                        <div className="space-y-1.5 mb-2 bg-gray-50 border border-gray-200 rounded-sm p-2" data-testid="wo-presets">
                            <div className="flex flex-wrap items-center gap-1.5" data-testid="wo-quick-positions">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mr-1">Vorschläge:</span>
                                {woQuick.map((p) => (
                                    <button key={p.label} type="button" onClick={() => appendWoPosition(p)}
                                        className="text-[11px] font-bold px-2 py-1 rounded-full border border-gray-300 bg-white text-gray-700 hover:border-[#111827] inline-flex items-center gap-1"
                                        data-testid={`wo-quick-${p.label}`}>
                                        <Plus className="w-3 h-3" /> {p.label}{Number(p.unit_price_net) > 0 ? ` · ${fmtEur(p.unit_price_net)}` : ""}
                                    </button>
                                ))}
                            </div>
                            {/* Aug-2026 (User-Wunsch): Lohn-Satz-Hinweis ausgeblendet. */}
                            {woFrequent.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1.5" data-testid="wo-frequent-positions">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mr-1 inline-flex items-center gap-1">
                                        <TrendingUp className="w-3 h-3" /> Häufig{vehicle.category ? ` (${vehicle.category})` : ""}:
                                    </span>
                                    {woFrequent.map((p, i) => (
                                        <button key={`${p.description}-${i}`} type="button" onClick={() => appendWoPosition(p)}
                                            className="text-[11px] font-bold px-2 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center gap-1"
                                            title={`${p.count}× genutzt`} data-testid={`wo-frequent-${i}`}>
                                            <Plus className="w-3 h-3" /> {p.label}{Number(p.unit_price_net) > 0 ? ` · ${fmtEur(p.unit_price_net)}` : ""}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {/* Jul-2026 (User-Wunsch): Vorlagen-Zeile & Gutschein entfernt —
                                Gutschein-Einlösung nur noch beim Rechnungschreiben. */}
                        </div>
                        <div className="space-y-2">
                            {positions.map((p, i) => (
                                <PositionRow
                                    key={i}
                                    pos={p}
                                    idx={i}
                                    articles={articles}
                                    onChange={(k, v) => setPos(i, k, v)}
                                    onRemove={() => removePos(i)}
                                    onLinkArticle={(art) => linkArticle(i, art)}
                                    onScan={(code) => resolveScan(i, code)}
                                    workOrderId={order?.id || null}
                                    warrantyManufacturer={warrantyManufacturer}
                                    suppliersById={suppliersById}
                                />
                            ))}
                        </div>
                        {/* Aug-2026 (User-Wunsch): Teile-Suche im Lieferanten-Portal bei den Positionen */}
                        <div className="mt-2">
                            {/* Aug-2026 (User-Wunsch): Direktsprung in Lieferanten-Portale mit Marke/Modell */}
                            {portalLinks.length > 0 && (
                                <div className="flex items-center gap-1.5 flex-wrap" data-testid="wo-portal-links">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Teile-Suche im Lieferanten-Portal:</span>
                                    {portalLinks.map((l) => (
                                        <a key={l.domain} href={l.url} target="_blank" rel="noreferrer"
                                            className="inline-flex items-center gap-1 text-[11px] font-bold border border-emerald-400 text-emerald-800 hover:bg-emerald-50 rounded-sm px-2 py-1"
                                            title={`${l.label} nach ${vehicle.brand || ""} ${vehicle.model || ""} durchsuchen (öffnet Portal — Login des Standorts verwenden)`}
                                            data-testid={`wo-portal-link-${l.domain}`}>
                                            {l.label} ↗
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Jul-2026 (User-Wunsch): Hinzufüge-Leiste UNTER der Positionsliste */}
                        <div className="mt-3" data-testid="wo-addbar-bottom">
                            <WwsAddBar
                                header={<label className="text-xs font-bold uppercase tracking-wider text-gray-700">Positionen</label>}
                                headerActions={
                                    <div className="flex gap-1.5 relative">
                                        <Button type="button" size="sm" variant="outline" onClick={() => addPos("part")} className="h-8 text-[11px] rounded-sm" data-testid="wo-add-free">
                                            <Plus className="w-3 h-3 mr-1" /> Freie Position
                                        </Button>
                                        <Button type="button" size="sm" variant="outline" onClick={() => addPos("labour")}
                                            className="h-8 text-[11px] rounded-sm border-amber-300 text-amber-800 hover:bg-amber-50"
                                            title={awRate ? `Lohn-Position mit ${awRate.net.toFixed(2).replace(".", ",")} €/AW netto aus den Einstellungen` : "Lohn-Position hinzufügen"}
                                            data-testid="wo-add-labour">
                                            <Plus className="w-3 h-3 mr-1" /> Lohn (AW)
                                        </Button>
                                        {/* Aug-2026 (User-Wunsch): Partner-Leistung NUR bei laufendem Leasingvertrag */}
                                        {usageInfo?.leasing && (
                                        <Button type="button" size="sm" variant="outline"
                                            onClick={() => { setPartnerSvcOpen((o) => !o); loadPartnerServices(); }}
                                            className="h-8 text-[11px] rounded-sm border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                                            title="Standard-Leistung eines Partnerunternehmens (Preisliste) als Position übernehmen — Leasingfahrzeug"
                                            data-testid="wo-add-partner-service">
                                            <Handshake className="w-3 h-3 mr-1" /> Partner-Leistung
                                        </Button>
                                        )}
                                        {usageInfo?.leasing && partnerSvcOpen && (
                                            <div className="absolute right-0 top-9 z-30 w-96 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-sm shadow-lg p-1" data-testid="wo-partner-service-menu">
                                                {partnerServices === null ? (
                                                    <div className="p-2 text-xs text-gray-500">Lade Partner-Leistungen…</div>
                                                ) : partnerServices.length === 0 ? (
                                                    <div className="p-2 text-xs text-gray-500">Keine Partner-Leistungen hinterlegt — Preisliste unter „Partnerunternehmen&quot; pflegen.</div>
                                                ) : partnerServices.map((e, i) => (
                                                    <button key={i} type="button" onClick={() => addPartnerService(e.partner, e.item)}
                                                        className="w-full text-left px-2 py-1.5 hover:bg-indigo-50 rounded-sm text-xs flex justify-between gap-2"
                                                        data-testid={`wo-partner-service-${i}`}>
                                                        <span className="min-w-0 truncate"><span className="font-bold">{e.item.name}</span><span className="text-gray-500"> · {e.partner.name}</span></span>
                                                        <span className="font-mono-num font-bold shrink-0">{fmtEur(e.item.price_net)}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                }
                                articles={articles} onAddArticle={addArticlePosition} onAddEan={addByEan} onCreateArticle={(q) => setNewArticle(/^\d{8,14}$/.test(q.trim()) ? { ean: q.trim(), category: "part" } : { name: q })} onAddFreeText={addFreeWoPosition} testidPrefix="wo-wws" />
                        </div>
                    </div>
                    </div>

{/* Aug-2026 (User-Wunsch): Folge-Auftrag/Teilen ÜBER Interner Notiz & Summen */}
                    {/* Aug-2026 (User-Wunsch): Abrechnungs-Aufteilung + Folge-Auftrag nebeneinander */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 items-start">
                    {/* Phase 4c+++ — Abrechnungs-Splitting-Headerfelder (Jun-2026: dezent eingeklappt, nicht editierbar) */}
                    {(positions.some((p) => p.billing_category === "warranty") || positions.some((p) => p.billing_category === "internal")) && (
                        <details className="bg-purple-50 border-l-4 border-purple-500 rounded-sm" data-testid="wo-billing-meta">
                            <summary className="cursor-pointer select-none px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-purple-900" data-testid="wo-billing-meta-toggle">
                                Abrechnungs-Aufteilung
                            </summary>
                            <div className="px-3 pb-3 space-y-2">
                            {positions.some((p) => p.billing_category === "warranty") && (
                                <div>
                                    <label className="text-xs font-bold text-purple-900 inline-flex items-center gap-1">Hersteller (Empfänger der Garantie-Rechnung) <InfoHint id="workshop.warranty_manufacturer" defaultText="Empfänger der separaten Garantie-Rechnung. Positionen mit Kategorie GARANTIE werden nicht dem Kunden, sondern diesem Hersteller/Importeur in Rechnung gestellt (Gewährleistungs-/Kulanzfall)." side="bottom" /></label>
                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                        <Input
                                            value={warrantyManufacturer}
                                            onChange={(e) => setWarrantyManufacturer(e.target.value)}
                                            placeholder="z.B. BMW Motorrad Deutschland GmbH"
                                            className="rounded-sm bg-white flex-1 min-w-[200px]"
                                            data-testid="wo-warranty-mfr"
                                        />
                                        {order?.id && (
                                            <a
                                                href={`${process.env.REACT_APP_BACKEND_URL}/api/work-orders/${order.id}/warranty-claim-pdf`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-purple-900 hover:text-purple-700 underline shrink-0"
                                                data-testid="wo-warranty-pdf-btn"
                                            >
                                                📄 Garantie-Antrag-PDF
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}
                            {positions.some((p) => p.billing_category === "internal") && (
                                <div>
                                    <label className="text-xs font-bold text-orange-900 inline-flex items-center gap-1">Eigenes Fahrzeug (Inserat-ID, auf das die interne Rechnung verbucht wird) <InfoHint id="workshop.internal_listing" defaultText="Inserat-ID/Kennzeichen des eigenen Lagerfahrzeugs. Positionen mit Kategorie INTERN werden als interne Kosten auf dieses Fahrzeug verbucht (z. B. Aufbereitung vor dem Verkauf) statt einem Kunden berechnet." side="bottom" /></label>
                                    <Input
                                        value={internalVehicleListingId}
                                        readOnly
                                        placeholder="— automatisch aus verknüpftem Fahrzeug —"
                                        className="rounded-sm mt-1 bg-gray-100 text-gray-600 cursor-not-allowed"
                                        data-testid="wo-internal-listing"
                                        title="Wird automatisch gesetzt und ist nicht editierbar."
                                    />
                                </div>
                            )}
                            <p className="text-[10px] text-purple-800 mt-1">
                                Bei Rechnungserstellung werden bis zu <strong>3 getrennte Rechnungs-Entwürfe</strong> erzeugt
                                — pro Kategorie eine eigene Rechnung mit dem entsprechenden Vermerk.
                            </p>
                            </div>
                        </details>
                    )}

                    {/* Feb-2026 (User-Wunsch) — Folge-Auftrag aus offenen Positionen.
                        Aug-2026 (User-Wunsch): Folge-Auftrag und „Auftrag teilen" als
                        ZWEI Bereiche nebeneinander. */}
                    {isEdit && positions.length > 0 && (
                        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2 items-start" data-testid="wo-followup-share-row">
                            <div className="bg-sky-50 border-l-4 border-sky-500 rounded-sm p-3 space-y-2" data-testid="wo-followup-block">
                                <button
                                    type="button"
                                    onClick={() => setFollowUpOpen((v) => !v)}
                                    className="w-full flex items-center justify-between gap-2 text-left min-w-0"
                                    data-testid="wo-followup-toggle"
                                >
                                    <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sky-900">
                                        <GitBranch className="w-4 h-4" /> Folge-Auftrag erstellen
                                    </span>
                                    <span className="text-[10px] text-sky-700">{followUpOpen ? "schließen" : "offene Arbeiten / Teile auslagern"}</span>
                                </button>
                                {followUpOpen && (
                                <div className="space-y-2" data-testid="wo-followup-panel">
                                    <p className="text-[11px] text-sky-800">
                                        Wähle Positionen, die <strong>noch nicht durchgeführt</strong> werden (Kunde kommt erneut)
                                        oder als <strong>Zubehör/Teile versendet</strong> werden müssen. Sie werden in einen neuen,
                                        verknüpften Folge-Auftrag übernommen.
                                    </p>
                                    <div className="space-y-1 max-h-44 overflow-y-auto rounded-sm bg-white border border-sky-200 p-2">
                                        {positions.map((p, i) => (
                                            <label key={i} className="flex items-center gap-2 text-xs cursor-pointer py-0.5" data-testid={`wo-followup-pos-${i}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={followUpSel.has(i)}
                                                    onChange={() => toggleFollowUpPos(i)}
                                                    className="accent-sky-600"
                                                    data-testid={`wo-followup-check-${i}`}
                                                />
                                                <span className="flex-1 truncate">{p.description || <span className="text-gray-400">(ohne Beschreibung)</span>}</span>
                                                <span className="text-[10px] uppercase tracking-wider text-gray-400">{p.type === "labour" ? "Arbeit" : p.type === "part" ? "Teil" : "Sonst."}</span>
                                            </label>
                                        ))}
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <Input
                                            value={followUpNote}
                                            onChange={(e) => setFollowUpNote(e.target.value)}
                                            placeholder="Notiz (z. B. Teil bestellt, KW 24)"
                                            className="rounded-sm bg-white text-sm"
                                            data-testid="wo-followup-note"
                                        />
                                        <div className="relative">
                                            <CalendarClock className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                            <Input
                                                type="date"
                                                value={followUpDate}
                                                onChange={(e) => setFollowUpDate(e.target.value)}
                                                className="rounded-sm bg-white text-sm pl-8"
                                                data-testid="wo-followup-date"
                                            />
                                        </div>
                                    </div>
                                    <label className="flex items-center gap-2 text-[11px] cursor-pointer text-sky-900">
                                        <input
                                            type="checkbox"
                                            checked={followUpRemove}
                                            onChange={(e) => setFollowUpRemove(e.target.checked)}
                                            className="accent-sky-600"
                                            data-testid="wo-followup-remove"
                                        />
                                        Ausgewählte Positionen aus diesem Auftrag entfernen
                                    </label>
                                    <Button
                                        type="button"
                                        onClick={createFollowUp}
                                        disabled={followUpBusy || followUpSel.size === 0}
                                        className="w-full bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-sm uppercase tracking-wider text-xs h-9"
                                        data-testid="wo-followup-create"
                                    >
                                        {followUpBusy
                                            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Erstelle…</>
                                            : <><GitBranch className="w-4 h-4 mr-1.5" /> Folge-Auftrag aus {followUpSel.size} Position{followUpSel.size !== 1 ? "en" : ""} erstellen</>}
                                    </Button>
                                </div>
                            )}
                            </div>
                            {/* Aug-2026 (User-Wunsch): „Auftrag teilen" kompakt — gleiche Höhe
                                wie der eingeklappte Folge-Auftrag, nur eine Klick-Zeile. */}
                            <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-sm p-3" data-testid="wo-share-block">
                                {order?.share_status !== "accepted" && !["invoiced", "cancelled"].includes(order?.status) ? (
                                    <button type="button" onClick={() => setShareOpen(true)}
                                        className="w-full flex items-center justify-between gap-2 text-left min-w-0"
                                        title="Auftrag an einen anderen Händler abgeben (Kapazitäts-Sharing)"
                                        data-testid="wo-share-btn">
                                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-indigo-900">
                                            <Share2 className="w-4 h-4" /> Auftrag teilen{order?.share_status === "pending" ? " ⏳" : ""}
                                        </span>
                                        <span className="text-[10px] text-indigo-700">an einen anderen Händler abgeben</span>
                                    </button>
                                ) : (
                                    <div className="w-full flex items-center justify-between gap-2">
                                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-indigo-900">
                                            <Share2 className="w-4 h-4" /> Auftrag teilen
                                        </span>
                                        <span className="text-[10px] text-indigo-500" data-testid="wo-share-unavailable">
                                            {order?.share_status === "accepted" ? "Bereits an einen Partner abgegeben." : "Für abgerechnete/stornierte Aufträge nicht verfügbar."}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    </div>


                    {/* Totals + Internal Note */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} className="rounded-sm min-h-[80px]" placeholder="Interne Notiz (nicht auf Auftrag/Rechnung)" data-testid="wo-internal" />
                        <div className="bg-[#111827] text-white rounded-sm p-3">
                            <div className="flex justify-between text-xs mb-1">
                                <span className="uppercase tracking-wider text-gray-400">Netto</span>
                                <span className="font-mono-num">{fmtEur(totals.net)}</span>
                            </div>
                            {(() => {
                                const discNet = positions.reduce((s, p) => {
                                    const net = (Number(p.quantity) || 0) * (Number(p.unit_price_net) || 0);
                                    return s + (net > 0 ? net * ((Number(p.discount_percent) || 0) / 100) : 0);
                                }, 0);
                                if (discNet <= 0) return null;
                                const base = totals.net + discNet;
                                const pct = base > 0 ? (discNet / base * 100) : 0;
                                return (
                                    <div className="flex justify-between text-xs mb-1 text-red-400 font-bold" data-testid="wo-discount-total">
                                        <span className="uppercase tracking-wider">enth. Rabatt (−{pct.toFixed(pct % 1 === 0 ? 0 : 1)}%)</span>
                                        <span className="font-mono-num">− {fmtEur(discNet)}</span>
                                    </div>
                                );
                            })()}
                            <div className="flex justify-between text-xs mb-2">
                                <span className="uppercase tracking-wider text-gray-400">USt</span>
                                <span className="font-mono-num">{fmtEur(totals.vat)}</span>
                            </div>
                            <div className="border-t border-gray-700 pt-2 flex justify-between items-baseline">
                                <span className="text-xs uppercase tracking-wider font-bold">Brutto</span>
                                <span className="font-mono-num font-black text-2xl">{fmtEur(totalGross)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Juni-2026 — Werkstatt-Provision live (aus WMS-Kategorie-% + Verteilung) */}
                    {commissionTotal > 0 && (
                        <div className="bg-[#FFCC00]/10 border border-[#FFCC00]/60 rounded-sm p-3" data-testid="wo-commission">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold uppercase tracking-wider text-[#111827] inline-flex items-center gap-1.5">
                                    <Percent className="w-4 h-4" /> Provision (Auftrag)
                                </span>
                                <span className="font-mono-num font-black text-lg text-[#111827]" data-testid="wo-commission-total">{fmtEur(commissionTotal)}</span>
                            </div>
                            <div className="text-[11px] text-gray-600 mt-1">
                                {commByCat.labour > 0 && <span>Lohn: {fmtEur(commByCat.labour)} </span>}
                                {commByCat.part > 0 && <span>· Teile: {fmtEur(commByCat.part)} </span>}
                            </div>
                            {commissionDist.length > 0 && (
                                <div className="text-[11px] text-gray-600 mt-1 flex flex-wrap gap-x-2" data-testid="wo-commission-dist">
                                    {commissionDist.map((d) => (
                                        <span key={d.k}>{d.label}: <span className="font-bold">{fmtEur(d.eur)}</span> ({d.pct}%)</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Jul-2026 (User-Wunsch): Lager-Abbuchungs-Hinweis entfernt. */}

                    {!isEdit && (() => {
                        const selC = customers.find((x) => x.id === customerId) || {};
                        const blockedInfo = selC.blocked
                            ? `Kunde ist GESPERRT${selC.blocked_reason ? ` (${selC.blocked_reason})` : ""} — es wird keine Bestätigung versendet.`
                            : selC.marketing_blocked
                                ? "DSGVO-Widerspruch — der Kunde darf NICHT per E-Mail, WhatsApp oder SMS kontaktiert werden. Es wird keine Bestätigung versendet."
                                : null;
                        return (
                            <AutoContactHint
                                previewUrl={`/work-orders/confirmation-preview${customerId ? `?customer_id=${customerId}` : ""}`}
                                skipActive={skipConfirmation}
                                onToggleSkip={setSkipConfirmation}
                                blockedInfo={blockedInfo}
                                testid="wo-save-flow-hint">
                                Beim Speichern wird der Auftrag <strong>verbindlich bestätigt</strong>, die <strong>Werkstattkopie gedruckt</strong> und automatisch eine <strong>Bestätigung an den Kunden</strong> (E-Mail, WhatsApp, SMS) inkl. Ihrer AGB &amp; Datenschutzhinweise gesendet.
                            </AutoContactHint>
                        );
                    })()}

                    {/* Aug-2026 (User-Wunsch): „Unterschriebenes Kunden-Exemplar" entfernt —
                        der Dokumenten-Posteingang erkennt unterschriebene Werkstattaufträge
                        automatisch und hängt sie an den Auftrag (Zahnrad-Regel). */}

                    <div className="flex flex-col sm:flex-row gap-2">
                        {/* Aug-2026 (User-Wunsch): „Auftrag aktualisieren" entfernt —
                            Auto-Speichern übernimmt das Aktualisieren automatisch. */}
                        {!isEdit && (
                        <Button type="submit" disabled={busy} className="flex-1 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider" data-testid="wo-submit">
                            {busy ? "Speichere…" : "Speichern · Drucken · Bestätigen"}
                        </Button>
                        )}
                        {isEdit && onDirectInvoice && !order?.invoice_id && (
                            <Button type="button" onClick={directInvoice} disabled={busy}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-sm uppercase tracking-wider sm:w-auto"
                                title="Speichert die Änderungen und öffnet direkt die Rechnungserstellung"
                                data-testid="wo-direct-invoice-btn">
                                <FileText className="w-4 h-4 mr-1.5" /> Rechnung
                            </Button>
                        )}
                        {/* Aug-2026 (User-Wunsch Jörg): manueller Versand — v. a. für interne
                            Aufträge, die keine automatischen Mails mehr auslösen. */}
                        {isEdit && (
                            <Button type="button" disabled={busy || confirmSending}
                                onClick={async () => {
                                    setConfirmSending(true);
                                    try {
                                        const r = await api.post(`/work-orders/${order.id}/resend-confirmation`);
                                        const ch = r.data?.confirmation?.channels || {};
                                        const ok = ["email", "whatsapp", "sms"].filter((k) => ch[k]?.ok);
                                        toast.success(ok.length
                                            ? `Bestätigung gesendet: ${ok.join(", ")}`
                                            : "Versand angestoßen — Kunde ohne Kontaktdaten?");
                                    } catch (e) {
                                        toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
                                    } finally {
                                        setConfirmSending(false);
                                    }
                                }}
                                className="bg-[#111827] hover:bg-black text-white font-black rounded-sm uppercase tracking-wider sm:w-auto"
                                title="Kunden-Bestätigung (E-Mail/WhatsApp/SMS) jetzt manuell senden — interne Aufträge versenden nie automatisch"
                                data-testid="wo-send-confirmation-btn">
                                {confirmSending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Status senden
                            </Button>
                        )}
                        {isEdit && (
                            <Button type="button" variant="outline" onClick={() => setProtoOpen(true)}
                                className="border-2 rounded-sm font-black uppercase tracking-wider sm:w-auto" data-testid="wo-handover-protocol-btn">
                                <FileSignature className="w-4 h-4 mr-1.5" /> Unterschreiben
                            </Button>
                        )}
                        {isEdit && (
                            <Button type="button" variant="outline" onClick={handlePrintBundle} disabled={printBusy}
                                className="border-2 rounded-sm font-black uppercase tracking-wider sm:w-auto" data-testid="wo-print-bundle-btn"
                                title="Druckt Monteur-Exemplar, Kunden-Exemplar zur Unterschrift und das QR-Code-Blatt">
                                {printBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Printer className="w-4 h-4 mr-1.5" />} Drucken
                            </Button>
                        )}
                    </div>
                    {isEdit && autoSavedAt && (
                        <div className="flex justify-end mt-1.5">
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700" data-testid="wo-autosaved-hint" title="Änderungen werden automatisch gespeichert (1,5 s nach der letzten Eingabe) — kein Aktualisieren-Button nötig">
                                <Check className="w-3.5 h-3.5" /> Automatisch gespeichert {autoSavedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                        </div>
                    )}
                </form>
            </div>

            <RecommendPartnerDialog
                open={showRecommend}
                onClose={() => setShowRecommend(false)}
                defaultCustomer={customers.find((c) => c.id === customerId) || null}
            />
            {shareOpen && isEdit && (
                <ShareOrderModal
                    order={order}
                    onClose={() => setShareOpen(false)}
                    onShared={() => { setShareOpen(false); onSaved?.(); }}
                />
            )}
            {protoOpen && isEdit && (
                <HandoverProtocolModal
                    orderId={order.id}
                    orderNumber={order.order_number}
                    onClose={() => setProtoOpen(false)}
                />
            )}
            {newArticle && (
                <ArticleModal
                    article={newArticle}
                    suppliers={[]}
                    onClose={() => setNewArticle(null)}
                    onSaved={(saved) => { setNewArticle(null); if (saved) addArticlePosition(saved); }}
                />
            )}
        </div>
    );
}

// Jun-2026 (User-Wunsch) — Warenwirtschafts-UNIVERSALSUCHE für Positionen.
// Jul-2026 (User-Wunsch): Beschreibung + WWS-Suche sind EIN Feld — tippen sucht
// live in der Warenwirtschaft (Teile & Leistungen), freier Text bleibt möglich;
// bei Auswahl ruft `onPick(article)` → füllt Beschreibung/Preis/Einheit + Lager-Bezug.
function ArticleSearchCombobox({ idx, text, onText, linkedSku, onPick, boldText }) {
    const [dirty, setDirty] = useState(false);
    const [focused, setFocused] = useState(false);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const query = dirty && focused ? (text || "") : "";
    const priceNet = (a) => Number(a.sell_price_net || (a.sell_price_gross / (1 + (a.vat_rate || 19) / 100)) || 0);
    const stockState = (a) => {
        if (a.category === "labour") return null;
        const qty = Number(a.stock_qty || 0);
        const min = Number(a.min_stock || 0);
        if (qty <= 0) return { label: "Nicht vorrätig", cls: "bg-red-100 text-red-800 border-red-300" };
        if (min > 0 && qty <= min) return { label: "Niedrigbestand", cls: "bg-amber-100 text-amber-900 border-amber-300" };
        return null;
    };
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) { setResults([]); setLoading(false); return; }
        let active = true;
        setLoading(true);
        const h = setTimeout(async () => {
            try {
                const r = await api.get("/articles", { params: { search: q, active_only: true, limit: 30 } });
                if (active) setResults(Array.isArray(r.data) ? r.data : (r.data?.items || []));
            } catch { if (active) setResults([]); }
            finally { if (active) setLoading(false); }
        }, 250);
        return () => { active = false; clearTimeout(h); };
    }, [query]);
    const pick = (a) => { onPick(a); setResults([]); setDirty(false); setFocused(false); };
    const showDropdown = focused && dirty && query.trim().length >= 2;
    return (
        <div className="relative flex-1" data-testid={`wo-pos-art-search-${idx}`}>
            {loading && <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin absolute right-2.5 top-1/2 -translate-y-1/2" />}
            <Input
                value={text || ""}
                onChange={(e) => { setDirty(true); onText(e.target.value); }}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => { setFocused(false); setDirty(false); }, 180)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (showDropdown && results[0]) pick(results[0]); } }}
                placeholder="Beschreibung — tippen sucht in der Warenwirtschaft…"
                className={`rounded-sm pr-8 ${boldText ? "font-bold" : ""}`}
                data-testid={`wo-pos-desc-${idx}`}
            />
            {linkedSku && !showDropdown && (
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5 pointer-events-none" data-testid={`wo-pos-linked-${idx}`}>
                    WWS {linkedSku}
                </span>
            )}
            {showDropdown && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-lg max-h-72 overflow-y-auto">
                    {loading && results.length === 0 ? (
                        <div className="px-3 py-2.5 text-xs text-gray-400">Suche…</div>
                    ) : results.length === 0 ? (
                        <div className="px-3 py-2.5 text-xs text-gray-400">Kein Treffer.</div>
                    ) : results.map((a) => {
                        const st = stockState(a);
                        return (
                            <button
                                key={a.id}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); pick(a); }}
                                className="w-full text-left px-3 py-2 hover:bg-[#FFFBEB] flex flex-col border-b border-gray-100 last:border-0"
                                data-testid={`wo-pos-art-opt-${a.id}`}
                            >
                                <span className="font-medium text-sm flex items-center gap-1.5">
                                    <Badge className={`shrink-0 text-[9px] font-bold uppercase rounded-sm ${a.category === "labour" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-800"}`}>{a.category === "labour" ? "Lohn" : "Teil"}</Badge>
                                    {a.name}
                                    {st && (
                                        <Badge className={`shrink-0 text-[9px] font-bold uppercase rounded-sm border ${st.cls}`} data-testid={`wo-pos-art-opt-stockbadge-${a.id}`}>
                                            {st.label}
                                        </Badge>
                                    )}
                                </span>
                                <span className="text-[10px] text-gray-500">
                                    {a.sku} · {fmtEur(priceNet(a))} netto{a.category === "labour" ? "" : ` · Bestand ${fmtNum(a.stock_qty)}`}{a.replaced_number ? ` · ersetzt ${a.replaced_number}` : ""}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function PositionRow({ pos, idx, articles, onChange, onRemove, onLinkArticle, onScan, workOrderId, warrantyManufacturer, suppliersById }) {
    const isLabour = pos.type === "labour";
    const [scanOpen, setScanOpen] = useState(false);
    // Jun-2026 (User-Wunsch) — nicht vorrätige Ersatzteile auf die Bestellliste setzen.
    const linkedArt = !isLabour && pos.article_id ? (articles || []).find((a) => a.id === pos.article_id) : null;
    // Aug-2026 (User-Wunsch) — Bestand des EIGENEN Standorts (org-stock) hat Vorrang
    // vor dem evtl. org-fremden Artikel-Bestand aus der Suche.
    const [ownStock, setOwnStock] = useState(null);
    const [orgLocs, setOrgLocs] = useState(null);
    const [transferBusy, setTransferBusy] = useState(false);
    const stockQty = ownStock != null ? ownStock : (linkedArt ? Number(linkedArt.stock_qty || 0) : null);
    const needQty = Number(pos.quantity || 0) || 1;
    const outOfStock = !isLabour && pos.article_id != null && stockQty != null && stockQty < needQty;
    useEffect(() => { setOwnStock(null); setOrgLocs(null); }, [pos.article_id]);
    useEffect(() => {
        // org-stock immer laden, sobald ein Teil verknüpft ist — liefert den
        // Bestand des EIGENEN Standorts (auch wenn der Artikel nicht in der
        // Parent-Artikelliste steckt) + Umlagerungs-Quellen.
        if (isLabour || !pos.article_id || orgLocs !== null) return;
        api.get(`/articles/${pos.article_id}/org-stock`).then((r) => {
            const self = (r.data.locations || []).find((l) => l.is_self);
            if (self) setOwnStock(Number(self.stock_qty));
            setOrgLocs((r.data.locations || []).filter((l) => !l.is_self && Number(l.stock_qty) > 0));
        }).catch(() => setOrgLocs([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLabour, pos.article_id, orgLocs]);
    const quickTransfer = async (loc) => {
        const qty = Math.min(Math.max(1, Math.ceil(needQty - (stockQty || 0))), Number(loc.stock_qty));
        setTransferBusy(true);
        try {
            const { data } = await api.post(`/articles/${pos.article_id}/quick-transfer`, {
                from_dealer_id: loc.dealer_id, quantity: qty,
            });
            setOwnStock(Number(data.own_stock));
            setOrgLocs((arr) => (arr || []).map((l) => l.dealer_id === loc.dealer_id
                ? { ...l, stock_qty: Number(data.source_stock) } : l).filter((l) => Number(l.stock_qty) > 0));
            toast.success(`${qty} ${pos.unit || "Stück"} von ${loc.site_code} umgelagert — Bestand jetzt hier verfügbar`, {
                duration: 8000,
                action: {
                    label: "Begleitschein (PDF)",
                    onClick: () => previewPdfRequest(
                        api.get(`/quick-transfers/${data.transfer_id}/pdf`, { responseType: "blob" }),
                        `Umlagerung_${pos.article_sku || ""}.pdf`),
                },
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Umlagerung fehlgeschlagen");
        }
        setTransferBusy(false);
    };
    const [reorderBusy, setReorderBusy] = useState(false);
    const [reordered, setReordered] = useState(false);
    const addToReorder = async () => {
        if (!linkedArt) return;
        setReorderBusy(true);
        try {
            await api.post("/wms/reorder-list", {
                article_id: linkedArt.id,
                article_name: linkedArt.name || pos.description || "Artikel",
                sku: linkedArt.sku || pos.article_sku || null,
                ean: linkedArt.ean || null,
                supplier_id: linkedArt.supplier_id || null,
                quantity: Math.max(1, needQty - Math.max(0, stockQty || 0)),
                work_order_id: workOrderId || null,
                warranty: billingCat === "warranty",
                warranty_manufacturer: billingCat === "warranty" ? (warrantyManufacturer || null) : null,
            });
            setReordered(true);
            if (billingCat === "warranty") {
                toast.warning(`Garantie-Teil: Bestellung muss beim Hersteller${warrantyManufacturer ? ` (${warrantyManufacturer})` : ""} erfolgen — nicht über N+F/Alternativen!`, { duration: 6000 });
            }
            toast.success("Auf Bestellliste gesetzt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Konnte nicht auf Bestellliste gesetzt werden");
        }
        setReorderBusy(false);
    };
    const billingCat = pos.billing_category || "customer";
    // 4 Bereiche (Jun-2026 User-Wunsch): Kunde grau · Garantie rot · Gebrauchtwagen blau · Auslieferung gelb
    const catTone = billingCat === "warranty"
        ? "bg-red-100 text-red-900 border-red-300"
        : billingCat === "gw"
            ? "bg-blue-100 text-blue-900 border-blue-300"
            : billingCat === "internal"
                ? "bg-amber-100 text-amber-900 border-amber-300"
                : "bg-gray-100 text-gray-800 border-gray-300";
    return (
        <div className={`border rounded-sm p-2.5 ${isLabour ? "bg-blue-50/30 border-blue-200" : "bg-gray-50 border-gray-200"} ${pos._auto_labour ? "ml-6 border-l-4 border-l-blue-400" : ""}`} data-testid={`wo-pos-${idx}`}>
            {pos._auto_labour && (
                <div className="text-[10px] text-blue-700 font-bold uppercase tracking-wider mb-1 flex items-center gap-1" data-testid={`wo-pos-autolabour-hint-${idx}`}>
                    ↳ Lohn zum Artikel darüber — AW eintragen oder mit ✕ entfernen (wird ohne AW nicht gespeichert)
                </div>
            )}
            <div className="flex gap-2 items-start mb-2">
                <Badge className={`shrink-0 ${isLabour ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-800"} text-[10px] font-bold uppercase rounded-sm`}>
                    {isLabour ? "Lohn" : pos.type === "other" ? "Sonst." : "Teil"}
                </Badge>
                {/* Jul-2026 (User-Wunsch) — Beschreibung + WWS-Suche sind EIN Feld
                    (vorher 3 redundante Suchfelder). Fett bei intern/Garantie. */}
                <ArticleSearchCombobox
                    idx={idx}
                    text={pos.description || ""}
                    onText={(v) => onChange("description", v)}
                    linkedSku={pos.article_sku}
                    boldText={billingCat !== "customer"}
                    onPick={onLinkArticle}
                />
                {/* Jul-2026 (User-Wunsch) — Artikelnummer IMMER anzeigen
                    (leeres Feld, wenn keine hinterlegt). */}
                {!isLabour && (
                    <Input
                        readOnly
                        value={pos.article_sku || ""}
                        placeholder="Art.-Nr. —"
                        title={pos.article_sku ? `Artikelnummer ${pos.article_sku}` : "Keine Artikelnummer hinterlegt"}
                        className="rounded-sm h-9 w-[110px] shrink-0 text-xs font-mono-num bg-gray-100 text-gray-700"
                        data-testid={`wo-pos-sku-${idx}`}
                    />
                )}
                {!isLabour && (
                    <Button type="button" size="sm" variant="outline" onClick={() => setScanOpen(true)}
                        className="rounded-sm shrink-0 px-2.5 border-[#111827]" title="Teil scannen (QR/Barcode) oder Artikelnummer eintippen" data-testid={`wo-pos-scan-${idx}`}>
                        <Camera className="w-3.5 h-3.5" />
                    </Button>
                )}
                <FrequentSelect
                    storageKey="workshop.position_billing_category"
                    value={billingCat}
                    onValueChange={(v) => onChange("billing_category", v)}
                    options={[
                        { value: "customer", label: "KUNDE" },
                        { value: "warranty", label: "GARANTIE" },
                        { value: "gw", label: "GEBRAUCHTW." },
                        { value: "internal", label: "AUSLIEFERUNG" },
                    ]}
                    triggerClassName={`text-[10px] font-bold uppercase tracking-wider rounded-sm h-9 w-[140px] shrink-0 ${catTone}`}
                    testId={`wo-pos-billing-${idx}`}
                />
                <Button type="button" size="sm" variant="outline" onClick={onRemove} className="rounded-sm shrink-0" data-testid={`wo-pos-del-${idx}`}>
                    <X className="w-3.5 h-3.5" />
                </Button>
            </div>
            <BarcodeScanDialog open={scanOpen} onClose={() => setScanOpen(false)} onDetected={(code) => { setScanOpen(false); onScan && onScan(code); }} />
            {/* Aug-2026 (User-Wunsch): Lieferanten-Verknüpfung + Lagerbestand am Teil sichtbar */}
            {(linkedArt || (!isLabour && ownStock != null)) && (
                <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-600" data-testid={`wo-pos-supplier-${idx}`}>
                    <span>Lager: <b className="font-mono-num">{stockQty}</b> {linkedArt?.unit || pos.unit || "Stück"}</span>
                    <span>Lieferant: <b>{(suppliersById && suppliersById[linkedArt?.supplier_id]?.name) || "— nicht hinterlegt"}</b></span>
                    {!outOfStock && linkedArt && (
                        <button type="button" disabled={reorderBusy || reordered} onClick={addToReorder}
                            className="inline-flex items-center gap-1 font-bold text-orange-800 hover:underline disabled:opacity-50"
                            title="Position auf die Bestellliste setzen"
                            data-testid={`wo-pos-reorder-inline-${idx}`}>
                            <ShoppingCart className="w-3 h-3" /> {reordered ? "Auf Bestellliste ✓" : "Auf Bestellliste"}
                        </button>
                    )}
                </div>
            )}
            {outOfStock && (
                <div className="mb-2 bg-orange-50 border border-orange-200 rounded-sm px-2.5 py-1.5" data-testid={`wo-pos-oos-${idx}`}>
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-bold text-orange-800 inline-flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5" /> Nicht am Lager an diesem Standort (Bestand {stockQty} {linkedArt?.unit || pos.unit || "Stück"}, benötigt {needQty})
                        </span>
                        <Button type="button" size="sm" variant="outline" disabled={!linkedArt || reorderBusy || reordered} onClick={addToReorder}
                            className="h-7 text-[11px] rounded-sm border-orange-400 text-orange-800 hover:bg-orange-100 font-bold shrink-0" data-testid={`wo-pos-reorder-${idx}`}>
                            <ShoppingCart className="w-3 h-3 mr-1" /> {reordered ? "Auf Bestellliste ✓" : "Auf Bestellliste"}
                        </Button>
                    </div>
                    {(orgLocs || []).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] font-bold" data-testid={`wo-pos-org-stock-${idx}`}>
                            <span className="text-emerald-800">An anderen Standorten verfügbar:</span>
                            {orgLocs.slice(0, 4).map((loc) => (
                                <button key={loc.dealer_id} type="button" onClick={() => quickTransfer(loc)} disabled={transferBusy}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                                    data-testid={`wo-pos-transfer-${idx}-${loc.site_code}`}>
                                    <ArrowLeftRight className="w-3 h-3" />
                                    {transferBusy ? "…" : `Von ${loc.site_code} umlagern (${Number(loc.stock_qty).toLocaleString("de-DE")} verfügbar)`}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {/* Jul-2026 (User-Wunsch): Überschriften als Platzhalter in den Feldern, USt-Feld entfernt (Standard intern). */}
            <div className="grid grid-cols-4 gap-2">
                <Input inputMode="decimal" placeholder={isLabour ? "AW" : "Menge"} title={isLabour ? "AW — Komma oder Punkt möglich (z. B. 7,5 oder 7.5)" : "Menge (0,1-Schritte, z. B. Liter) — Komma oder Punkt möglich"} value={pos.quantity ?? ""} onChange={(e) => onChange("quantity", e.target.value.replace(",", "."))} className="rounded-sm h-8 text-sm" data-testid={`wo-pos-qty-${idx}`} />
                {pos.type === "labour" ? (
                    <div className="h-8 flex items-center justify-center rounded-sm border border-gray-200 bg-gray-50 text-sm font-bold text-gray-600 select-none"
                        title="Einheit nicht änderbar: echter Lohn = AW (1 AW = 6 Minuten) · Dienstleistungen = Stück"
                        data-testid={`wo-pos-unit-aw-${idx}`}>
                        {(pos.article_id && pos.unit && pos.unit !== "AW") ? "Stück" : "AW"}
                    </div>
                ) : (
                    <Input placeholder="Einheit" title="Einheit" value={pos.unit || ""} onChange={(e) => onChange("unit", e.target.value)} className="rounded-sm h-8 text-sm" />
                )}
                <Input type="number" step="0.01" placeholder="EP netto €" title="EP netto €" value={pos.unit_price_net ?? ""} onChange={(e) => onChange("unit_price_net", e.target.value)} onBlur={() => { if (pos._min_price_net != null && Number(pos.unit_price_net) < Number(pos._min_price_net)) { onChange("unit_price_net", pos._min_price_net); toast.error(`Vorgegebener Artikelpreis (${Number(pos._min_price_net).toFixed(2)} €) darf nicht unterschritten werden — Preissenkung nur über Rabatt. Nach oben ist der Preis frei anpassbar.`); } }} className="rounded-sm h-8 text-sm" data-testid={`wo-pos-price-${idx}`} />
                <Input type="number" min="0" max="100" placeholder="Rabatt %" title="Rabatt %" value={pos.discount_percent || ""} onChange={(e) => onChange("discount_percent", e.target.value)} className="rounded-sm h-8 text-sm" />
            </div>
            {!isLabour && (
                <Textarea
                    value={pos.damage_note || ""}
                    onChange={(e) => onChange("damage_note", e.target.value)}
                    className="rounded-sm mt-2 min-h-[40px] text-sm"
                    placeholder="Schadensdokumentation / Befund (optional)"
                />
            )}
            {/* Aug-2026 (User-Wunsch): AW-Summe + Schadens-Fotos nebeneinander in einer Zeile */}
            {(workOrderId || isLabour) && (
                <div className="mt-1.5 flex flex-wrap items-start justify-between gap-2">
                    {isLabour && Number(pos.quantity) > 0 && Number(pos.unit_price_net) > 0 && (() => {
                        const net = (Number(pos.quantity) || 0) * (Number(pos.unit_price_net) || 0) * (1 - (Number(pos.discount_percent) || 0) / 100);
                        const gross = net * (1 + (Number(pos.vat_rate) || 19) / 100);
                        return (
                            <div className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-800 bg-blue-50 border border-blue-200 rounded-sm px-2 py-1 self-center" data-testid={`wo-pos-aw-sum-${idx}`}>
                                {Number(pos.quantity)} {pos.unit || "AW"} × {fmtEur(Number(pos.unit_price_net))} = {fmtEur(net)} netto
                                {Number(pos.discount_percent) > 0 ? ` (−${Number(pos.discount_percent)} %)` : ""} · {fmtEur(gross)} brutto
                            </div>
                        );
                    })()}
                    {workOrderId && (
                        <DamagePhotoSection
                            workOrderId={workOrderId}
                            positionIdx={idx}
                            images={pos.damage_images || []}
                            onChange={(imgs) => onChange("damage_images", imgs)}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

function DamagePhotoSection({ workOrderId, positionIdx, images, onChange }) {
    const [uploading, setUploading] = useState(false);
    const handleUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const r = await api.post(
                `/work-orders/${workOrderId}/positions/${positionIdx}/damage-images`,
                form,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            onChange([...(images || []), r.data.url]);
            toast.success("Bild hochgeladen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
        }
        setUploading(false);
    };
    const removeImage = async (url) => {
        if (!window.confirm("Bild entfernen?")) return;
        try {
            await api.delete(
                `/work-orders/${workOrderId}/positions/${positionIdx}/damage-images?url=${encodeURIComponent(url)}`,
            );
            onChange((images || []).filter((u) => u !== url));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
        }
    };
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    return (
        <div className="flex-1 min-w-[240px] max-w-md ml-auto" data-testid={`wo-pos-damage-section-${positionIdx}`}>
            <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider font-bold text-gray-800">
                    Schadens-Fotos ({(images || []).length})
                </span>
                <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1.5 rounded-sm shadow-sm cursor-pointer"
                    data-testid="wo-pos-photo-upload-label">
                    <Camera className="w-3.5 h-3.5" />
                    {uploading ? "Lade hoch…" : "Foto aufnehmen / hochladen"}
                    <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        disabled={uploading}
                        onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
                        className="hidden"
                        data-testid={`wo-pos-photo-input-${positionIdx}`}
                    />
                </label>
            </div>
            {(images || []).length > 0 && (
                <div className="grid grid-cols-4 gap-1.5">
                    {images.map((url) => (
                        <div key={url} className="relative group">
                            <img
                                src={url.startsWith("http") ? url : `${apiBase}${url}`}
                                alt="Schaden"
                                className="w-full h-16 object-cover rounded-sm border border-gray-300"
                            />
                            <button
                                type="button"
                                onClick={() => removeImage(url)}
                                className="absolute top-0.5 right-0.5 bg-black/70 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`wo-pos-photo-del-${positionIdx}`}
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default WorkOrderModal;
